import * as turf from '@turf/helpers';
import turfNearestPoint from '@turf/nearest-point';
import turfNearestPointOnLine from '@turf/nearest-point-on-line';
import pointToLineDistance from '@turf/point-to-line-distance';
import { Feature, LineString, Point, Polygon } from 'geojson';
import transform from 'geojson-transformer';
import iziToast, { IziToastSettings } from 'izitoast';
import * as mapboxgl from 'mapbox-gl';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import * as geolib from 'geolib';
import Schema from 'schema.io';
import * as uniqid from 'uniqid';
import {
  clearVirtualGroupLink,
  createNewVirtualGroup, getVirtualGroupData,
  updateVirtualGroup,
} from '../api/operation/geojson';
import GraphInfoPanelControl from '../classes/controls/GraphInfoPanelControl';
// import OriginalPointsLegendControl from '../classes/controls/OriginalPointsLegendControl';
// import PanelControl from '../classes/controls/PanelControl';
// import RegionListControl from '../classes/controls/RegionListControl';
import RegionInfoControl from '../classes/controls/RegionInfoControl';
import PolygonGenerator from '../classes/generators/PolygonGenerator';
import RoadNetGenerator from '../classes/generators/RoadNetGenerator';
import AdjustmentLoader from '../classes/loaders/AdjustmentLoader';
import BacklogPointLoader from '../classes/loaders/BacklogPointLoader';
import ForbiddenAreasLoader from '../classes/loaders/ForbiddenAreasLoader';
import HeatMapLoader from '../classes/loaders/HeatMapLoader';
import InspectionAreasLoader from '../classes/loaders/InspectionAreasLoader';
import OriginalPointsLoader from '../classes/loaders/OriginalPointsLoader';
import ParkPointLoader from '../classes/loaders/ParkPointLoader';
import PointToMeasureLoader from '../classes/loaders/PointToMeasureLoader';
import PointToPhotoLoader from '../classes/loaders/PointToPhotoLoader';
import PolygonsLoader from '../classes/loaders/PolygonsLoader';
import ProhibitedAreasLoader from '../classes/loaders/ProhibitedAreasLoader';
import RegionsLoader from '../classes/loaders/RegionsLoader';
import RoadLoader from '../classes/loaders/RoadLoader';
import RoadNetLoader from '../classes/loaders/RoadNetLoader';

import {
  LS_MAP_CENTER_AND_ZOOM_KEY,
  MLID_ADJUSTMENT_POINT,
  MLID_ADJUSTMENT_POINT_FINISHED,
  MLID_AFTER_STOCK_PLACE,
  MLID_BACKLOG_CIRCLE,
  MLID_BACKLOG_POINT,
  MLID_BEFORE_STOCK_PLACE,
  MLID_DISPATCH_WORK_ORDER,
  MLID_FORBIDDEN_AREAS,
  MLID_FORBIDDEN_AREAS_EDITOR,
  MLID_HEATMAP_POINT,
  MLID_HEATMAP_POLYGON,
  MLID_INSPECTION_AREAS,
  MLID_ORIGINAL_POINTS,
  MLID_ORIGINAL_POINTS_FOCUSED,
  MLID_PARK_POINT_STOCK,
  MLID_PARK_POINT_USER,
  MLID_POINT_TO_MEASURE,
  MLID_POINT_TO_PHOTO,
  MLID_POLYGON_EDITOR,
  MLID_POLYGON_GENERATOR_NAVILINE,
  MLID_POLYGON_GENERATOR_OUTLINE,
  MLID_POLYGONS,
  MLID_PROHIBITED_AREAS,
  MLID_REGION_BUFFER,
  MLID_REGION_EDITOR,
  MLID_REGION_EDITOR_POLYGON,
  MLID_REGIONS,
  MLID_REMOTE_FORBIDDENS,
  MLID_REMOTE_POLYGONS,
  MLID_REMOTE_POLYGONS_BUFFER,
  MLID_REMOTE_POLYGONS_NO_REGION,
  MLID_REMOTE_POLYGONS_NO_REGION_BUFFER,
  MLID_ROAD,
  MLID_ROAD_FURNITURE,
  MLID_ROAD_LANE_DIVIDER,
  MLID_ROAD_NET,
  MLID_ROAD_NET_EDITOR,
  MLID_ROAD_NET_EDITOR_LOCAL,
  MLID_ROAD_NET_GENERATOR_NAVILINE,
  MLID_ROAD_NET_GENERATOR_OUTLINE,
  MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT,
  MSID_ADJUSTMENT_POINT,
  MSID_ADJUSTMENT_POINT_FINISHED,
  MSID_AFTER_STOCK_PLACE,
  MSID_BACKLOG_CIRCLE,
  MSID_BACKLOG_POINT,
  MSID_BEFORE_STOCK_PLACE,
  MSID_DISPATCH_WORK_ORDER,
  MSID_FORBIDDEN_AREAS,
  MSID_FORBIDDEN_AREAS_EDITOR,
  MSID_HEATMAP,
  MSID_INSPECTION_AREAS,
  MSID_ORIGINAL_POINTS,
  MSID_ORIGINAL_POINTS_FOCUSED,
  MSID_PARK_POINT_STOCK,
  MSID_PARK_POINT_USER,
  MSID_POINT_TO_MEASURE,
  MSID_POINT_TO_PHOTO,
  MSID_POLYGON_EDITOR,
  MSID_POLYGON_GENERATOR_NAVILINE,
  MSID_POLYGON_GENERATOR_OUTLINE,
  MSID_POLYGONS,
  MSID_PROHIBITED_AREAS,
  MSID_REGION_BUFFER,
  MSID_REGION_EDITOR,
  MSID_REGION_EDITOR_POLYGON,
  MSID_REGIONS,
  MSID_REMOTE_FORBIDDENS,
  MSID_REMOTE_POLYGONS,
  MSID_REMOTE_POLYGONS_BUFFER,
  MSID_REMOTE_POLYGONS_NO_REGION,
  MSID_REMOTE_POLYGONS_NO_REGION_BUFFER,
  MSID_ROAD,
  MSID_ROAD_FURNITURE,
  MSID_ROAD_LANE_DIVIDER,
  MSID_ROAD_NET,
  MSID_ROAD_NET_EDITOR,
  MSID_ROAD_NET_EDITOR_LOCAL,
  MSID_ROAD_NET_GENERATOR_NAVILINE,
  MSID_ROAD_NET_GENERATOR_OUTLINE,
  MSID_ROAD_NET_REMOTE_AND_LOCAL_POINT,
} from '../utils/constants';

import { backlogFunction } from '../views/popUp/BacklogPoint';
import PhotoView from '../views/popUp/PhotoView';
import { removeFunction } from '../views/popUp/Remove';
import UploadPhotoView from '../views/popUp/UploadPhotoView';
import EventData = mapboxgl.EventData;
import GeoJSONSource = mapboxgl.GeoJSONSource;
import LngLat = mapboxgl.LngLat;

import LngLatBoundsLike = mapboxgl.LngLatBoundsLike;
import ScaleControl = mapboxgl.ScaleControl;
import getDistanceSimple = geolib.getDistanceSimple;
import DispatchWorkOrderLoader from '../classes/loaders/DispatchWorkOrderLoader';
import mapStore from '../storeInstances/mapStore';
import SelectMapTileStore from './SelectMapTileStore';

// https://stackoverflow.com/questions/44332290/mapbox-gl-typing-wont-allow-accesstoken-assignment
(mapboxgl as any).accessToken = 'pk.eyJ1IjoibWlzZXJ5bGVlIiwiYSI6ImNqOWdza3JwcDMxcWozMnJ3dG54NnQ5bXEifQ.Q9Cyr-2AwPwfvy-WqFGf7g';

interface ICenterAndZoom {
  center: ILngLat;
  zoom: number;
}

const centerAndZoomSchema = new Schema({
  center: [Number],
  zoom: Number,
});

const originalPointsLoader = new OriginalPointsLoader();
const polygonGenerator = new PolygonGenerator();
const polygonsLoader = new PolygonsLoader();
const regionsLoader = new RegionsLoader();
const parkPointLoader = new ParkPointLoader();
const pointToMeasureLoader = new PointToMeasureLoader();
const prohibitedAreasLoader = new ProhibitedAreasLoader();
const forbiddenAreasLoader = new ForbiddenAreasLoader();
const pointToPhotoLoader = new PointToPhotoLoader();
const inspectionAreasLoader = new InspectionAreasLoader();
const roadNetLoader = new RoadNetLoader();
const roadNetGenerator = new RoadNetGenerator();
const roadLoader = new RoadLoader();
const backlogPointLoader = new BacklogPointLoader();
const adjustmentLoader = new AdjustmentLoader();
const heatMapLoader = new HeatMapLoader();
const dispatchWorkOrderLoader = new DispatchWorkOrderLoader();

const getMouseInteractionBounds = (point: { x: number, y: number }, width: number = 5) => {
  return [
    [point.x - width, point.y - width],
    [point.x + width, point.y + width],
  ];
};

const emptyPointFeature: Feature<Point> = {
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: [],
  },
  properties: {},
};

export default class MapStore {

  public id = uniqid();
  public editable: boolean = true;
  public enableAddParkingLot: boolean = false;
  public enableAddForbiddenAreas: boolean = false;
  public enableAddRoadNet: boolean = false;
  public enableAddBacklogPoint: boolean = false;
  public pressingAltKey: boolean = false;
  public pressingCtrlKey: boolean = false;
  public pressingEKey: boolean = false;
  public pressingUKey: boolean = false;
  public multiPolygonSelector: any[] = [];
  public isEditParkLot: boolean = false;
  public isEditForbidden: boolean = false;
  public isEditRoadNet: boolean = false;
  public isEditMeasurePoint: boolean = false;
  public isEditPhotoPoint: boolean = false;
  public isEditBacklogPoint: boolean = false;
  public geometryInfo?: IObject = undefined;
  public zoom: number = -1;
  public center: ILngLat = [116.46636478900007, 39.982904261000044];
  public minEditableZoom: number = 10;
  public isMapTileVisible: boolean = true;
  public isParkPointVisible: boolean = false;
  public isProhibitedAreaVisible: boolean = true;
  public isForbiddenAreaVisible: boolean = true;
  public isInspectionAreaVisible: boolean = true;
  public isParkLotVisible: boolean = true;
  public isRegionVisible: boolean = true;
  public isRoadNetVisibility: boolean = true;
  public isRoadNetNodesVisibility: boolean = true;
  public isPointsToMeasureVisible: boolean = false;
  public isPointsToPhotoVisible: boolean = false;
  public isBacklogVisible: boolean = true;
  public isHeatMapVisible: boolean = true;
  public isDispatchWorkOrderVisible: boolean = true;
  public isAdjustmentVisible: boolean = true;
  public roadNetDefaultRoadType: number = 0;
  public adjustmentRemark: string = '';
  public adjustmentDefaultState: number = 0;
  public virtualGroupForm = {
    _id: '',
    name: '',
    enableOverageLimit: true,
    overageLimitCount: 0,
    stockBelowAlarmCount: -1,
    stockBacklogAlarmCount: -1,
    sort: 999,
    oldSort: 999,
  };

  private _map?: mapboxgl.Map;
  private _intersectLayers: string[] = [];
  private _draggingNode: boolean = false;
  private _focusingOriginalPoint?: Feature<Point>;

  private _popup?: mapboxgl.Popup;
  private _popupRoot: Element = document.createElement('div');
  private _lastMoveEndPoint?: { latitude: number; longitude: number };

  private static get _centerAndZoom() {
    const data = localStorage.getItem(LS_MAP_CENTER_AND_ZOOM_KEY);
    if (data) {
      try {
        return centerAndZoomSchema.validate(JSON.parse(data)) as ICenterAndZoom;
      } catch (err) {
        // noop.
      }
    }
    return {
      center: [116.46636478900007, 39.982904261000044],
      zoom: 18,
    };
  }

  private static set _centerAndZoom(centerAndZoom: ICenterAndZoom) {
    localStorage.setItem(LS_MAP_CENTER_AND_ZOOM_KEY, JSON.stringify(centerAndZoom));
  }

  constructor() {
    roadNetLoader._roadNetDefaultRoadType = this.roadNetDefaultRoadType;
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Alt':
          this.pressingAltKey = true;
          break;
        case 'Control':
          this.pressingCtrlKey = true;
          break;
        case 'Meta':
          this.pressingCtrlKey = true;
          break;
        case 'Shift':
          if (this._map) {
            // 切换矢量图和影像图
            const vecVisible = this._map.getLayoutProperty('tianditu-vec-layer', 'visibility');
            if (vecVisible === 'visible') {
              // 切换到影像图
              this._map.setLayoutProperty('tianditu-vec-layer', 'visibility', 'none');
              this._map.setLayoutProperty('tianditu-cva-layer', 'visibility', 'none');
              this._map.setLayoutProperty('tianditu-img-layer', 'visibility', 'visible');
              this._map.setLayoutProperty('tianditu-cia-layer', 'visibility', 'visible');
            } else {
              // 切换到矢量图
              this._map.setLayoutProperty('tianditu-vec-layer', 'visibility', 'visible');
              this._map.setLayoutProperty('tianditu-cva-layer', 'visibility', 'visible');
              this._map.setLayoutProperty('tianditu-img-layer', 'visibility', 'none');
              this._map.setLayoutProperty('tianditu-cia-layer', 'visibility', 'none');
            }
          }
          break;
        case 'e':
          this.pressingEKey = true;
          break;
        case 'u':
          this.pressingUKey = true;
        default:
      }
    });
    document.addEventListener('keyup', (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Alt':
          this.pressingAltKey = false;
          break;
        case 'Control':
          this.pressingCtrlKey = false;
          break;
        case 'Meta':
          this.pressingCtrlKey = false;
          break;
        case 'e':
          this.pressingEKey = false;
          break;
        case 'u':
          this.pressingUKey = false;
        default:
      }
    });
    document.addEventListener('keypress', (e: KeyboardEvent) => {
      switch (e.code) {
        case 'Space':
          if (this.editable) {
            this._setUneditable();
          } else if (this._map && this._map.getZoom() >= this.minEditableZoom) {
            this.editable = true;
          }
          break;
        default:
      }
    });
  }

  public isEditPointsToMeasure() {
    this._initToggleEnableAdd();
    if (!this.isEditMeasurePoint) {
      this._initEditState();
    }
    this.isEditMeasurePoint = !this.isEditMeasurePoint;
  }

  public isEditBacklogFunction() {
    this._initToggleEnableAdd();
    if (!this.isEditBacklogPoint) {
      this._initEditState();
    }
    this.isEditBacklogPoint = !this.isEditBacklogPoint;
  }

  public isEditRoadNetLine() {
    this._initToggleEnableAdd();
    if (!this.isEditRoadNet) {
      this._initEditState();
    }
    this.isEditRoadNet = !this.isEditRoadNet;
  }

  public isEditParkLotArea() {
    this._initToggleEnableAdd();
    if (!this.isEditParkLot) {
      this._initEditState();
    }
    this.isEditParkLot = !this.isEditParkLot;
  }

  public isEditForbiddenArea() {
    this._initToggleEnableAdd();
    if (!this.isEditForbidden) {
      this._initEditState();
    }
    this.isEditForbidden = !this.isEditForbidden;
  }

  public isEditPointsToPhoto() {
    this._initToggleEnableAdd();
    if (!this.isEditPhotoPoint) {
      this._initEditState();
    }
    this.isEditPhotoPoint = !this.isEditPhotoPoint;
  }

  public fitToBounds(bounds: LngLatBoundsLike) {
    if (this._map) {
      this._map.fitBounds(bounds, {
        padding: 16,
      });
    }
  }

  public setVisibility(id: string, state: boolean) {
    if (this._map) {
      this._map.setLayoutProperty(id, 'visibility', state ? 'visible' : 'none');
    }
  }

  public toggleParkPointLayerVisibility() {
    if (this._map) {
      const visible = !this.isParkPointVisible;
      this._map.setLayoutProperty(MLID_PARK_POINT_STOCK, 'visibility', visible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_PARK_POINT_USER, 'visibility', visible ? 'visible' : 'none');
      this.isParkPointVisible = visible;
    }
  }

  public toggleBacklogLayerVisibility() {
    if (this._map) {
      const visible = !this.isBacklogVisible;
      this._map.setLayoutProperty(MLID_BACKLOG_POINT, 'visibility', visible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_BACKLOG_CIRCLE, 'visibility', visible ? 'visible' : 'none');
      this.isBacklogVisible = visible;
    }
  }

  public toggleAdjustmentVisible() {
    if (this._map) {
      this.isAdjustmentVisible = !this.isAdjustmentVisible;
      this._map.setLayoutProperty(MLID_ADJUSTMENT_POINT, 'visibility', this.isAdjustmentVisible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_ADJUSTMENT_POINT_FINISHED, 'visibility', this.isAdjustmentVisible ? 'visible' : 'none');
    }
  }

  public toggleIsMapTileVisible() {
    if (this._map) {
      this.isMapTileVisible = !this.isMapTileVisible;
      this._map.setLayoutProperty('tiles-layer', 'visibility', this.isMapTileVisible ? 'visible' : 'none');
    }
  }

  public togglePointsToMeasureVisibility() {
    if (this._map) {
      this.isPointsToMeasureVisible = !this.isPointsToMeasureVisible;
      this._map.setLayoutProperty(MLID_POINT_TO_MEASURE, 'visibility', this.isPointsToMeasureVisible ? 'visible' : 'none');
    }
  }

  public toggleRoadNetNodesVisibility() {
    if (this._map) {
      this.isRoadNetNodesVisibility = !this.isRoadNetNodesVisibility;
      this._map.setLayoutProperty(MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT, 'visibility', this.isRoadNetNodesVisibility ? 'visible' : 'none');
    }
  }

  public togglePointsToPhotoVisibility() {
    if (this._map) {
      this.isPointsToPhotoVisible = !this.isPointsToPhotoVisible;
      this._map.setLayoutProperty(MLID_POINT_TO_PHOTO, 'visibility', this.isPointsToPhotoVisible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_AFTER_STOCK_PLACE, 'visibility', this.isPointsToPhotoVisible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_BEFORE_STOCK_PLACE, 'visibility', this.isPointsToPhotoVisible ? 'visible' : 'none');
    }
  }

  public toggleProhibitedAreaVisible() {
    if (this._map) {
      this.isProhibitedAreaVisible = !this.isProhibitedAreaVisible;
      this._map.setLayoutProperty(MLID_PROHIBITED_AREAS, 'visibility', this.isProhibitedAreaVisible ? 'visible' : 'none');
    }
  }

  public toggleForbiddenAreaVisible() {
    if (this._map) {
      this.isForbiddenAreaVisible = !this.isForbiddenAreaVisible;
      this._map.setLayoutProperty(MLID_FORBIDDEN_AREAS, 'visibility', this.isForbiddenAreaVisible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_REMOTE_FORBIDDENS, 'visibility', this.isForbiddenAreaVisible ? 'visible' : 'none');
    }
  }

  public toggleRegionVisible() {
    if (this._map) {
      this.isRegionVisible = !this.isRegionVisible;
      this._map.setLayoutProperty(MLID_REGIONS, 'visibility', this.isRegionVisible ? 'visible' : 'none');
    }
  }

  public toggleParkLotVisible() {
    if (this._map) {
      this.isParkLotVisible = !this.isParkLotVisible;
      this._map.setLayoutProperty(MLID_REMOTE_POLYGONS_BUFFER, 'visibility', this.isParkLotVisible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_REMOTE_POLYGONS, 'visibility', this.isParkLotVisible ? 'visible' : 'none');
    }
  }

  public toggleInspectionAreaVisible() {
    if (this._map) {
      this.isInspectionAreaVisible = !this.isInspectionAreaVisible;
      this._map.setLayoutProperty(MLID_INSPECTION_AREAS, 'visibility', this.isInspectionAreaVisible ? 'visible' : 'none');
    }
  }

  public toggleRoadNetVisibility() {
    if (this._map) {
      this.isRoadNetVisibility = !this.isRoadNetVisibility;
      this._map.setLayoutProperty(MLID_ROAD_NET, 'visibility', this.isRoadNetVisibility ? 'visible' : 'none');
    }
  }

  public toggleEnableAddParkingLot() {
    this._initEditState();
    if (!this.enableAddParkingLot) {
      this._initToggleEnableAdd();
    }
    this.enableAddParkingLot = !this.enableAddParkingLot;
    polygonGenerator.cancelGenerating();
  }

  public toggleEnableAddRoadNet() {
    this._initEditState();
    if (!this.enableAddRoadNet) {
      this._initToggleEnableAdd();
    }
    this.enableAddRoadNet = !this.enableAddRoadNet;
    roadNetGenerator.cancelGenerating();
  }

  public toggleEnableBacklogPoint() {
    this._initEditState();
    if (!this.enableAddBacklogPoint) {
      this._initToggleEnableAdd();
    }
    this.enableAddBacklogPoint = !this.enableAddBacklogPoint;
  }

  public toggleHeatMapVisibility() {
    if (this._map) {
      const visible = !this.isHeatMapVisible;
      this._map.setLayoutProperty(MLID_HEATMAP_POINT, 'visibility', visible ? 'visible' : 'none');
      this._map.setLayoutProperty(MLID_HEATMAP_POLYGON, 'visibility', visible ? 'visible' : 'none');
      this.isHeatMapVisible = visible;
    }
  }

  public toggleDispatchWorkOrderVisibility() {
    if (this._map) {
      const visible = !this.isDispatchWorkOrderVisible;
      this._map.setLayoutProperty(MLID_DISPATCH_WORK_ORDER, 'visibility', visible ? 'visible' : 'none');
      this.isDispatchWorkOrderVisible = visible;
    }
  }
  public selectRoadNetRoadType(roadType: number) {
    this.roadNetDefaultRoadType = roadType;
    roadNetLoader._roadNetDefaultRoadType = roadType;
  }

  public containerMounted() {
    const NEWMapToken = (process.env || {}).NODE_ENV === 'development' ?
        '3e15a524695630763fd809efb289f591dbd1550dac7da0a2102b845f4f5936c8' :
        '249136121fcb29d49f069be17bc7f03db65df224c537bb47693e2d3c48e8ebf9';
    const map = this._map = new mapboxgl.Map({
      container: this.id,
      style: {
        version: 8,
        sources: {
          // 天地图矢量底图
          'tianditu-vec-source': {
            type: 'raster',
            tiles: [
                `https://tiles1.geovisearth.com/base/v1/vec/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles2.geovisearth.com/base/v1/vec/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles3.geovisearth.com/base/v1/vec/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
              // 'http://t0.tianditu.gov.cn/DataServer?T=vec_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t1.tianditu.gov.cn/DataServer?T=vec_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t2.tianditu.gov.cn/DataServer?T=vec_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
            ],
            tileSize: 256,
            maxzoom: 18,  // 瓦片服务最大级别
          },
          // // 天地图矢量注记
          'tianditu-cva-source': {
            type: 'raster',
            tiles: [
              // 'http://t0.tianditu.gov.cn/DataServer?T=cva_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t1.tianditu.gov.cn/DataServer?T=cva_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t2.tianditu.gov.cn/DataServer?T=cva_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
                `https://tiles1.geovisearth.com/base/v1/cat/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles2.geovisearth.com/base/v1/cat/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles3.geovisearth.com/base/v1/cat/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
            ],
            tileSize: 256,
            maxzoom: 18,  // 瓦片服务最大级别
          },
          // 天地图影像底图
          'tianditu-img-source': {
            type: 'raster',
            tiles: [
              // 'http://t0.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t1.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t2.tianditu.gov.cn/DataServer?T=img_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
                `https://tiles1.geovisearth.com/base/v1/img/{z}/{x}/{y}?format=webp&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles2.geovisearth.com/base/v1/img/{z}/{x}/{y}?format=webp&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles3.geovisearth.com/base/v1/img/{z}/{x}/{y}?format=webp&tmsIds=w&token=${NEWMapToken}`,
            ],
            tileSize: 256,
            maxzoom: 17,  // 瓦片服务最大级别
          },
          // 天地图影像注记
          'tianditu-cia-source': {
            type: 'raster',
            tiles: [
              // 'http://t0.tianditu.gov.cn/DataServer?T=cia_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t1.tianditu.gov.cn/DataServer?T=cia_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
              // 'http://t2.tianditu.gov.cn/DataServer?T=cia_w&x={x}&y={y}&l={z}&tk=804afb7eccdd4b00916f47d05c368c7b',
                `https://tiles1.geovisearth.com/base/v1/cia/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles2.geovisearth.com/base/v1/cia/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
                `https://tiles3.geovisearth.com/base/v1/cia/{z}/{x}/{y}?format=png&tmsIds=w&token=${NEWMapToken}`,
            ],
            tileSize: 256,
            maxzoom: 17,  // 瓦片服务最大级别
          },
        },
        layers: [{
          id: 'background',
          type: 'background',
          paint: {
            'background-color': '#f0f0f0',
          },
          layout: {
            visibility: 'visible',
          },
        }, {
          // 天地图矢量底图图层
          id: 'tianditu-vec-layer',
          type: 'raster',
          source: 'tianditu-vec-source',
          minzoom: 0,
          maxzoom: 22,  // 允许放大到22级（通过overzoom）
          layout: {
            visibility: 'visible',
          },
        }, {
          // 天地图矢量注记图层（叠加在矢量底图上）
          id: 'tianditu-cva-layer',
          type: 'raster',
          source: 'tianditu-cva-source',
          minzoom: 0,
          maxzoom: 22,  // 允许放大到22级（通过overzoom）
          layout: {
            visibility: 'visible',
          },
        }, {
          // 天地图影像底图图层
          id: 'tianditu-img-layer',
          type: 'raster',
          source: 'tianditu-img-source',
          minzoom: 0,
          maxzoom: 22,  // 允许放大到22级（通过overzoom）
          layout: {
            visibility: 'none',
          },
        }, {
          // 天地图影像注记图层（叠加在影像底图上）
          id: 'tianditu-cia-layer',
          type: 'raster',
          source: 'tianditu-cia-source',
          minzoom: 0,
          maxzoom: 22,  // 允许放大到22级（通过overzoom）
          layout: {
            visibility: 'none',
          },
        }],
        sprite: 'https://mangoebike.oss-cn-shenzhen.aliyuncs.com/image/sprite',
        glyphs: 'https://mangoebike.com/glyphs/{fontstack}/{range}.pbf',
      },
      maxZoom: 22,
      ...MapStore._centerAndZoom,
      attributionControl: false,
    });
    this._popup = new mapboxgl.Popup({
      closeOnClick: false,
    });
    map.addControl(new ScaleControl());
    map.addControl(new GraphInfoPanelControl());
    map.addControl(new RegionInfoControl());
    // map.addControl(new OriginalPointsLegendControl());
    // map.addControl(new PanelControl());
    // map.addControl(new RegionListControl());

    map.on('moveend', () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      MapStore._centerAndZoom = {
        center: [center.lng, center.lat],
        zoom,
      };
    });
    regionsLoader.onMapLoad();
    map.on('load', this._afterLoaded.bind(this));
  }

  public refreshRemotePolygons() {
    polygonsLoader.onMapUpdatedBoundary(this._map!.getBounds().toArray() as [ILngLat, ILngLat]);
  }

  public async updateRatedParkingCapacity(parkingLotId: string, ratedParkingCapacity: number) {
    await polygonsLoader.updateRatedParkingCapacity(parkingLotId, ratedParkingCapacity).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error = {name: '', message: 'err'}) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  // 修改小于多少台触发告警
  public async updateStockBelowAlarmCount(parkingLotId: string, count: number) {
    await polygonsLoader.updateStockBelowAlarmCount(parkingLotId, count).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error = {name: '', message: 'err'}) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async updateHasBluetoothSpike(parkingLotId: string, ratedParkingCapacity: boolean) {
    await polygonsLoader.updateHasBluetoothSpike(parkingLotId, ratedParkingCapacity).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async updateEnableOverageLimit(parkingLotId: string, enableOverageLimit: boolean) {
    await polygonsLoader.updateEnableOverageLimit(parkingLotId, enableOverageLimit).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async updateOverageLimitCount(parkingLotId: string, overageLimitCount: number) {
    await polygonsLoader.updateOverageLimitCount(parkingLotId, overageLimitCount).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async updateStockBacklogAlarmCount(parkingLotId: string, overageLimitCount: number) {
    await polygonsLoader.updateStockBacklogAlarmCount(parkingLotId, overageLimitCount).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async updateParkingNameAndSave(parkingLotId: string, newName: string) {
    await polygonsLoader.updateParkingNameAndSave(parkingLotId, newName).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });

  }

  public updateParkingName(newName: string) {
    if (this.geometryInfo) {
      this.geometryInfo = {
        ...this.geometryInfo,
        name: newName || '',
        nameChanged: newName !== this.geometryInfo.oldName
      };
    }
  }

  public updateParkingSort(newSort: string) {
    if (this.geometryInfo) {
      const sortValue = parseInt(newSort, 10) || 999;
      this.geometryInfo = {
        ...this.geometryInfo,
        sort: sortValue,
        sortChanged: sortValue !== this.geometryInfo.oldSort
      };
    }
  }

  public async updateParkingSortAndSave(parkingLotId: string, sort: number, name: string) {
    await polygonsLoader.updateParkingSortAndSave(parkingLotId, sort, name).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public async clearVirtualGroupLink(parkingLotId: string, overageLimitCount: number) {
    await polygonsLoader.clearVirtualGroupLink(parkingLotId).then(() => {
      this.refreshRemotePolygons();
      polygonsLoader.on('remote_polygons_updated', () => {
        const parkingLot = polygonsLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
        this.geometryInfo = {... (parkingLot as Feature<Polygon>).properties!.info, layerType: 'parkLot'};
      });
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public updateVirtualGroupForm(form: Record<string, any>) {
    Object.assign(this.virtualGroupForm, form);
    this.multiPolygonSelector = JSON.parse(JSON.stringify(this.multiPolygonSelector));
  }

  public createVirtualGroup = (ids: string[], regionId: string) => {
    const data = JSON.parse(JSON.stringify({
      ...this.virtualGroupForm,
      ids,
      regionId,
    }));
    delete data._id;
    createNewVirtualGroup(data).then(res => {
      if (res.data && res.data._id) {
        alert('创建成功!');
        this.virtualGroupForm._id = res.data._id;
        this.virtualGroupForm.name = res.data.name || '未命名合并分区';
        this.virtualGroupForm.enableOverageLimit = res.data.enableOverageLimit;
        this.virtualGroupForm.overageLimitCount = res.data.overageLimitCount;
        this.virtualGroupForm.stockBelowAlarmCount = res.data.stockBelowAlarmCount || -1;
        this.virtualGroupForm.stockBacklogAlarmCount = res.data.stockBacklogAlarmCount || -1;
        this.virtualGroupForm.sort = res.data.sort || 999;
        this.virtualGroupForm.oldSort = res.data.sort || 999;
        this.multiPolygonSelector = JSON.parse(JSON.stringify(this.multiPolygonSelector));
      } else {
        alert('修改失败!');
      }
      this.refreshRemotePolygons();
    });
  }

  public editVirtualGroup = (ids: string[], regionId: string) => {
    const data = JSON.parse(JSON.stringify({
      ...this.virtualGroupForm,
      ids,
      regionId,
    }));
    updateVirtualGroup(data).then(res => {
      if (res.data && res.data._id) {
        alert('修改成功!');
        getVirtualGroupData(data._id).then(res => {
          this.virtualGroupForm.enableOverageLimit = res.data.enableOverageLimit;
          this.virtualGroupForm.overageLimitCount = res.data.overageLimitCount;
          this.virtualGroupForm.stockBelowAlarmCount = res.data.stockBelowAlarmCount || -1;
          this.virtualGroupForm.stockBacklogAlarmCount = res.data.stockBacklogAlarmCount || -1;
          this.virtualGroupForm.name = res.data.name || '未命名合并分区';
          this.virtualGroupForm.sort = res.data.sort || 999;
          this.virtualGroupForm.oldSort = res.data.sort || 999;
          this.multiPolygonSelector = JSON.parse(JSON.stringify(this.multiPolygonSelector));
        })
      } else {
        alert('修改失败!');
      }
      this.refreshRemotePolygons();
    });
  }

  public loadVirtualGroups = (virtualGroupId: string) => {
    getVirtualGroupData(virtualGroupId).then((res) => {
      this.virtualGroupForm._id = res.data._id;
      this.virtualGroupForm.name = res.data.name || '未命名合并分区';
      this.virtualGroupForm.enableOverageLimit = res.data.enableOverageLimit;
      this.virtualGroupForm.overageLimitCount = res.data.overageLimitCount;
      this.virtualGroupForm.stockBelowAlarmCount = res.data.stockBelowAlarmCount || -1;
      this.virtualGroupForm.stockBacklogAlarmCount = res.data.stockBacklogAlarmCount || -1;
      this.virtualGroupForm.sort = res.data.sort || 999;
      this.virtualGroupForm.oldSort = res.data.sort || 999;
      this.multiPolygonSelector.splice(0, this.multiPolygonSelector.length);
      this.multiPolygonSelector.push(...((res.data.ids || []).map((item: any) => {
        item.region = { _id: item.region };
        return JSON.stringify(item);
      }) || []));
      this.multiPolygonSelector = JSON.parse(JSON.stringify(this.multiPolygonSelector));
      this._updateMultiPolygonSelectorHelper();
    });
  }

  public async saveVirtualGroupSort(virtualGroupId: string, sort: number, name: string) {
    // 使用和停车区相同的接口保存虚拟分组的排序
    await polygonsLoader.updateParkingSortAndSave(virtualGroupId, sort, name).then(() => {
      // 更新本地状态
      this.virtualGroupForm.sort = sort;
      this.virtualGroupForm.oldSort = sort;
      // 刷新远程数据
      this.refreshRemotePolygons();
    }).catch((e: Error) => {
      iziToast.error({ title: e.message } as IziToastSettings);
    });
  }

  public refreshRemoteForbiddens() {
    forbiddenAreasLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat);
  }

  // public async updateRatedForbiddenCapacity(parkingLotId: string, ratedParkingCapacity: number) {
  //   console.log("lop")
  //   this.refreshRemoteForbiddens();
  //     forbiddenAreasLoader.on('remote_forbiddens_updated', () => {
  //       const forbiddens = forbiddenAreasLoader.geojsonRemotePolygons.features.find((polygon: any) => polygon.properties.id === parkingLotId) || {};
  //       this.geometryInfo = {... (forbiddens as Feature<Polygon>).properties!.info, layerType: 'forbiddenArea'};
  //     })
  // }

  public refreshRemoteRoadNet() {
    roadNetLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat);
  }

  public refreshBacklogPoint() {
    backlogPointLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat);
    backlogPointLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat);
  }

  public refreshAdjustment() {
    adjustmentLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat, this.adjustmentDefaultState);
  }

  public refreshHeatMap() {
    heatMapLoader.refresh();
  }

  public async refreshBacklogPointStock() {
    await backlogPointLoader.updateBacklogPointStock(this._map!.getCenter().toArray() as ILngLat);
    await backlogPointLoader.onMapUpdatedCenter(this._map!.getCenter().toArray() as ILngLat);
    const backlogPoint = backlogPointLoader.updateGeometryInfo(this.geometryInfo);
    this.geometryInfo = { ...backlogPoint, layerType: 'backlogPoint' };
  }

  public changeAdjustmentRemark(remark: string) {
    this.adjustmentRemark = remark;
  }

  public async auditAdjustmentRemark(adjustmentId: string, remark: string) {
    await adjustmentLoader.audit(adjustmentId, remark);
    const adjustment = await adjustmentLoader.findById(adjustmentId);
    this.geometryInfo = { ...adjustment, layerType: 'adjustment' };
    this._afterAuditAdjustmentRemark();
  }

  public selectAdjustmentState(state: number) {
    this.adjustmentDefaultState = state;
    this._afterAuditAdjustmentRemark();
    // roadNetLoader._roadNetDefaultRoadType = roadType;
  }

  private _afterAuditAdjustmentRemark() {
    const map = this._map!;
    adjustmentLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat, this.adjustmentDefaultState);
  }

  private _setUneditable() {
    this.editable = false;
    polygonsLoader.cancelEditing();
    forbiddenAreasLoader.cancelEditing();
    polygonGenerator.cancelGenerating();
    roadNetLoader.cancelEditing();
    roadNetGenerator.cancelGenerating();
  }

  private _initToggleEnableAdd() {
    this.enableAddParkingLot = false;
    this.enableAddRoadNet = false;
    this.enableAddBacklogPoint = false;
  }

  private _clearSelect() {
    polygonsLoader.cancelEditing();
    forbiddenAreasLoader.cancelEditing();
    roadNetLoader.cancelEditing();
  }

  private _initEditState() {
    this.isEditParkLot = false;
    this.isEditForbidden = false;
    this.isEditRoadNet = false;
    this.isEditMeasurePoint = false;
    this.isEditPhotoPoint = false;
    this.isEditBacklogPoint = false;
    polygonsLoader.cancelEditing();
    forbiddenAreasLoader.cancelEditing();
    polygonGenerator.cancelGenerating();
    roadNetLoader.cancelEditing();
    roadNetGenerator.cancelGenerating();
  }

  private _onMapMoveEnd() {
    const map = this._map!;
    const lastMoveEndPoint = this._lastMoveEndPoint;
    const center = {
      latitude: map.getCenter().lat,
      longitude: map.getCenter().lng,
    };
    const shouldTriggerLoad = !lastMoveEndPoint || getDistanceSimple(lastMoveEndPoint, center, 1) >= 100;
    if (!shouldTriggerLoad) {
      return;
    }
    this._lastMoveEndPoint = center;
    if (map.getZoom() >= 16) {
      this.refreshRemotePolygons();
      this.refreshRemoteForbiddens();
    } else if (map.getZoom() < this.minEditableZoom) {
      this._setUneditable();
    }
    pointToMeasureLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat);
    pointToPhotoLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat);
    backlogPointLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat);
    adjustmentLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat, this.adjustmentDefaultState);
    forbiddenAreasLoader.onMapUpdatedCenter(map.getCenter().toArray() as ILngLat);
  }

  private _afterLoaded() {
    const map = this._map!;
    prohibitedAreasLoader.onMapLoad();
    forbiddenAreasLoader.onMapLoad(this.center);
    inspectionAreasLoader.onMapLoad();
    heatMapLoader.onMapLoad();
    dispatchWorkOrderLoader.onMapLoad();
    this.refreshRemoteRoadNet();

    this._initBacklogLayer();
    this._initAdjustmentLayer();
    this._initHeatMap();

    this._initOriginalPointsLayer();
    this._initPolygonGeneratorLayer();
    this._initPolygonsLayer();
    this._initRemotePolygonsLayer();
    this._initRegionsLayer();
    this._initDemoLayer();
    this._initParkPointLayer();
    this._initPointToMeasureLayer();
    this._initPointToPhotoLayer();
    this._initProhibitedAreasLayer();
    this._initForbiddenAreasLayer();
    this._initRemoteForbiddenAreasLayer();
    this._initInspectionAreasLayer();
    this._initRoadNetLineStringLayer();
    this._initAfterStockPlaceLayer();
    this._initBeforeStockPlaceLayer();
    this._initRoadNetGeneratorLayer();
    this._initRoadLayer();
    this._initDispatchWorkOrder();

    roadLoader.on('updated', () => {
      (map.getSource(MSID_ROAD) as GeoJSONSource).setData(roadLoader.geojsonRoad);
      (map.getSource(MSID_ROAD_FURNITURE) as GeoJSONSource).setData(roadLoader.geojsonFurniture);
      (map.getSource(MSID_ROAD_LANE_DIVIDER) as GeoJSONSource).setData(roadLoader.geojsonLaneDivider);
    });

    backlogPointLoader.on('updated', () => {
      (map.getSource(MSID_BACKLOG_POINT) as GeoJSONSource).setData(backlogPointLoader.geojson);
      (map.getSource(MSID_BACKLOG_CIRCLE) as GeoJSONSource).setData(backlogPointLoader.geojsonBuffer);
    });
    adjustmentLoader.on('updated', () => {
      (map.getSource(MSID_ADJUSTMENT_POINT) as GeoJSONSource).setData(adjustmentLoader.unFinishedGeojson);
      (map.getSource(MSID_ADJUSTMENT_POINT_FINISHED) as GeoJSONSource).setData(adjustmentLoader.finishedGeojson);
    });
    heatMapLoader.on('updated', () => {
      (map.getSource(MSID_HEATMAP) as GeoJSONSource).setData(heatMapLoader.geojson);
    });

    dispatchWorkOrderLoader.on('updated', () => {
      (map.getSource(MSID_DISPATCH_WORK_ORDER) as GeoJSONSource).setData(dispatchWorkOrderLoader.geojson);
    });

    prohibitedAreasLoader.on('updated', () => {
      (map.getSource(MSID_PROHIBITED_AREAS) as GeoJSONSource).setData(prohibitedAreasLoader.geojson);
    });

    forbiddenAreasLoader.on('updated', () => {
      (map.getSource(MSID_FORBIDDEN_AREAS) as GeoJSONSource).setData(forbiddenAreasLoader.geojson);
    });
    forbiddenAreasLoader.on('forbidden_editor_updated', () => {
      (map.getSource(MSID_FORBIDDEN_AREAS_EDITOR) as GeoJSONSource).setData(forbiddenAreasLoader.geojsonEditor);
    });
    forbiddenAreasLoader.on('remote_forbiddens_updated', () => {
      (map.getSource(MSID_REMOTE_FORBIDDENS) as GeoJSONSource).setData(forbiddenAreasLoader.geojsonRemotePolygons);
    });

    inspectionAreasLoader.on('updated', () => {
      (map.getSource(MSID_INSPECTION_AREAS) as GeoJSONSource).setData(inspectionAreasLoader.geojson);
    });

    pointToMeasureLoader.on('points_updated', () => {
      (map.getSource(MSID_POINT_TO_MEASURE) as GeoJSONSource).setData(pointToMeasureLoader.geojson);
    });

    pointToPhotoLoader.on('photo_points_updated', () => {
      (map.getSource(MSID_POINT_TO_PHOTO) as GeoJSONSource).setData(pointToPhotoLoader.photoGeoJson);
      (map.getSource(MSID_AFTER_STOCK_PLACE) as GeoJSONSource).setData(pointToPhotoLoader.afterStockPlaceGeoJson);
      (map.getSource(MSID_BEFORE_STOCK_PLACE) as GeoJSONSource).setData(pointToPhotoLoader.beforeStockPlaceGeoJson);
    });

    parkPointLoader.on('updated', () => {
      (map.getSource(MSID_PARK_POINT_USER) as GeoJSONSource).setData(parkPointLoader.geojsonUser);
      (map.getSource(MSID_PARK_POINT_STOCK) as GeoJSONSource).setData(parkPointLoader.geojsonStock);
    });

    originalPointsLoader.on('updated', () => {
      (map.getSource(MSID_ORIGINAL_POINTS) as GeoJSONSource).setData(originalPointsLoader.geojson);
    });

    polygonGenerator.on('outline_updated', () => {
      (map.getSource(MSID_POLYGON_GENERATOR_OUTLINE) as GeoJSONSource).setData(polygonGenerator.geojsonOutline);
    });
    polygonGenerator.on('naviline_updated', () => {
      (map.getSource(MSID_POLYGON_GENERATOR_NAVILINE) as GeoJSONSource).setData(polygonGenerator.geojsonNaviline);
    });

    roadNetGenerator.on('outline_updated', () => {
      (map.getSource(MSID_ROAD_NET_GENERATOR_OUTLINE) as GeoJSONSource).setData(roadNetGenerator.geojsonOutline);
    });
    roadNetGenerator.on('naviline_updated', () => {
      (map.getSource(MSID_ROAD_NET_GENERATOR_NAVILINE) as GeoJSONSource).setData(roadNetGenerator.geojsonNaviline);
    });

    polygonsLoader.on('polygons_updated', () => {
      (map.getSource(MSID_POLYGONS) as GeoJSONSource).setData(polygonsLoader.geojsonPolygons);
    });
    polygonsLoader.on('polygon_editor_updated', () => {
      (map.getSource(MSID_POLYGON_EDITOR) as GeoJSONSource).setData(polygonsLoader.geojsonEditor);
    });
    polygonsLoader.on('remote_polygons_updated', () => {
      (map.getSource(MSID_REMOTE_POLYGONS) as GeoJSONSource).setData(polygonsLoader.geojsonRemotePolygons);
      (map.getSource(MSID_REMOTE_POLYGONS_NO_REGION) as GeoJSONSource).setData(polygonsLoader.geojsonRemoteNoRegionPolygons);
    });
    polygonsLoader.on('polygons_buffer_updated', () => {
      (map.getSource(MSID_REMOTE_POLYGONS_BUFFER) as GeoJSONSource).setData(polygonsLoader.geojsonRemotePolygonsBuffer);
      (map.getSource(MSID_REMOTE_POLYGONS_NO_REGION_BUFFER) as GeoJSONSource).setData(polygonsLoader.geojsonRemotePolygonsNoRegionBuffer);
    });

    regionsLoader.on('updated', () => {
      (map.getSource(MSID_REGIONS) as GeoJSONSource).setData(regionsLoader.geojson);
      (map.getSource(MSID_REGION_BUFFER) as GeoJSONSource).setData(regionsLoader.geojsonBuffer);
    });
    regionsLoader.on('editor_updated', () => {
      (map.getSource(MSID_REGION_EDITOR) as GeoJSONSource).setData(regionsLoader.geojsonEditor);
      (map.getSource(MSID_REGION_EDITOR_POLYGON) as GeoJSONSource).setData(regionsLoader.geojsonEditingRegion);
    });

    roadNetLoader.on('updated', () => {
      (map.getSource(MSID_ROAD_NET) as GeoJSONSource).setData(roadNetLoader.geojson);
    });
    roadNetLoader.on('editor_updated', () => {
      (map.getSource(MSID_ROAD_NET_EDITOR) as GeoJSONSource).setData(roadNetLoader.geojsonEditor);
    });

    roadNetLoader.on('local_updated', () => {
      (map.getSource(MSID_ROAD_NET_EDITOR_LOCAL) as GeoJSONSource).setData(roadNetLoader.geojsonEditLocalRoadNet);
    });

    roadNetLoader.on('points_updated', () => {
      (map.getSource(MSID_ROAD_NET_REMOTE_AND_LOCAL_POINT) as GeoJSONSource).setData(roadNetLoader.geojsonPoints);
    });

    map.on('click', (e: EventData) => {
      const geometryCache = this.geometryInfo ? JSON.stringify(this.geometryInfo) : null;
      const nodes = map.queryRenderedFeatures(e.point);
      if (nodes.length && nodes[0].properties!.info) {
        this.geometryInfo = JSON.parse(nodes[0].properties!.info);
        this.geometryInfo = {
          ...this.geometryInfo,
          oldName: (this.geometryInfo || {}).name || '',
          name: (this.geometryInfo || {}).name || '',
          nameChanged: false,
          oldSort: (this.geometryInfo || {}).sort || 999,
          sort: (this.geometryInfo || {}).sort || 999,
          sortChanged: false
        }
        console.log('this.geometryInfo name: ', this.geometryInfo.name, this.geometryInfo);
      } else {
        this.geometryInfo = undefined;
      }
      // 选择多个停车区
      if (this.pressingCtrlKey) {
        if (this.multiPolygonSelector.length === 0 && geometryCache) { this.multiPolygonSelector.push(geometryCache); }
        if (this.geometryInfo) {
          const currentId = this.geometryInfo._id;
          const hasIndex = this.multiPolygonSelector.findIndex((e) => JSON.parse(e)._id === currentId);
          if (hasIndex > -1) {
            this.multiPolygonSelector.splice(hasIndex, 1);
          } else {
            this.multiPolygonSelector.push(JSON.stringify(this.geometryInfo));
          }
        }
        this._updateMultiPolygonSelectorHelper();
      } else {
        this.multiPolygonSelector.splice(0, (this.multiPolygonSelector || []).length);
        this._updateMultiPolygonSelectorHelper();
        Object.assign(this.virtualGroupForm, {
          _id: '',
          name: '',
          enableOverageLimit: true,
          overageLimitCount: 0,
          stockBelowAlarmCount: 0,
          stockBacklogAlarmCount: 0,
        });
      }
      const dispatchNodes = map.queryRenderedFeatures(e.point, {layers: [MLID_DISPATCH_WORK_ORDER]});
      if (dispatchNodes.length && !this.pressingEKey) {
        const photoUrl = dispatchNodes[0].properties!.photo;
        const orderid = dispatchNodes[0].properties!.orderid;
        const createdAt = dispatchNodes[0].properties!.createdAt;
        ReactDOM.render(PhotoView(orderid, createdAt, photoUrl, ''), this._popupRoot);
        this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
      }

      const photoNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_POINT_TO_PHOTO] });
      if (photoNodes.length && !this.isEditPhotoPoint) {
        const photoUrl = photoNodes[0].properties!.photo;
        const creator = photoNodes[0].properties!.creator;
        const createdAt = photoNodes[0].properties!.createdAt;
        ReactDOM.render(PhotoView(creator, createdAt, photoUrl, '照片点'), this._popupRoot);
        this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
      }
      const stockPhotoNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_BEFORE_STOCK_PLACE, MLID_AFTER_STOCK_PLACE] });
      if (stockPhotoNodes.length) {
        const photoUrl = stockPhotoNodes[0].properties!.photo;
        const creator = stockPhotoNodes[0].properties!.creator;
        const createdAt = stockPhotoNodes[0].properties!.createdAt;
        const processed = stockPhotoNodes[0].properties!.processed;
        ReactDOM.render(PhotoView(creator, createdAt, photoUrl, processed ? '摆放后' : '摆放前'), this._popupRoot);
        this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
      }
      if (this.editable) {
        this._clearSelect();
        if (this.isEditParkLot) {
              const editorNodes = map.queryRenderedFeatures(e.point, {layers: [MLID_POLYGON_EDITOR]});
              if (editorNodes.length === 0) {
                const remotePolygons = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), {layers: [MLID_REMOTE_POLYGONS]});
                if (remotePolygons.length > 0) {
                  polygonsLoader.onMapClickedRemotePolygon((remotePolygons[0] as Feature<Polygon>).properties!.id);
                } else {
                  const polygons = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), {layers: [MLID_POLYGONS]});
                  if (polygons.length > 0) {
                    polygonsLoader.toggleSelectPolygon((polygons[0] as Feature<Polygon>).properties!.id);
                  }
                }
              }
        }

        if (this.isEditForbidden) {
            const editorNodes = map.queryRenderedFeatures(e.point, {layers: [MLID_FORBIDDEN_AREAS_EDITOR]});
            if (editorNodes.length === 0) {
              const remotePolygons = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), {layers: [MLID_REMOTE_FORBIDDENS]});
              if (this.pressingUKey) {
                forbiddenAreasLoader.unLockPolygon((remotePolygons[0] as Feature<Polygon>).properties!.id);
              } else {
                if (remotePolygons.length > 0) {
                  forbiddenAreasLoader.onMapClickedRemotePolygon((remotePolygons[0] as Feature<Polygon>).properties!.id);
                } else {
                  const polygons = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), {layers: [MLID_FORBIDDEN_AREAS]});
                  if (polygons.length > 0) {
                    forbiddenAreasLoader.toggleSelectPolygon((polygons[0] as Feature<Polygon>).properties!.id);
                  }
                }
              }
            }
          // this.refreshRemoteForbiddens();
        }

        if (this.enableAddBacklogPoint) {
          ReactDOM.render(backlogFunction((name: string, radius: number, warningNumber: number, lngLat: ILngLat) => {
            backlogPointLoader.create(name, radius, warningNumber, lngLat);
            this._popup!.remove();
          }, e.lngLat.toArray() as ILngLat), this._popupRoot);
          this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
        }
        if (this.enableAddParkingLot) {
          const originalPoints = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ORIGINAL_POINTS] });
          if (originalPoints.length > 0) {
            polygonGenerator.onMapClickedOriginalPoint((originalPoints[0] as Feature<Point>).geometry.coordinates as ILngLat);
          } else {
            polygonGenerator.onMapClickedOriginalPoint(e.lngLat.toArray() as ILngLat);
          }
        }
        if (this.isEditRoadNet) {
          const roadNetNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_ROAD_NET_EDITOR] });
          if (roadNetNodes.length === 0) {
            const remoteLineStrings = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET] });
            if (remoteLineStrings.length > 0) {
              roadNetLoader.onMapClickedRemoteLineString((remoteLineStrings[0] as Feature<LineString>).properties!.id);
            } else {
              const lineStrings = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_EDITOR_LOCAL] });
              if (lineStrings.length > 0) {
                roadNetLoader.selectLocalLineString((lineStrings[0] as Feature<LineString>).properties!.id);
              }
            }
          }
        }
        if (this.enableAddRoadNet) {
          const originalPoints = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT] });
          if (originalPoints.length > 0) {
            roadNetGenerator.onMapClickedOriginalPoint((originalPoints[0] as Feature<Point>).geometry.coordinates as ILngLat);
          } else {
            roadNetGenerator.onMapClickedOriginalPoint(e.lngLat.toArray() as ILngLat);
          }
        }
        if (this.isEditMeasurePoint) {
          pointToMeasureLoader.onMapClick(e.lngLat.toArray() as ILngLat);
        }
        if (this.isEditPhotoPoint) {
          ReactDOM.render(UploadPhotoView((evt: React.ChangeEvent<HTMLInputElement>, lngLat: LngLat) => {
            pointToPhotoLoader.onCreatePoint(evt, lngLat);
            this._popup!.remove();
          }, e.lngLat), this._popupRoot);
          this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
        }
      }

      this._refreshCursor();
    });

    map.on('contextmenu', (e: EventData) => {
      if (this.isEditMeasurePoint) {
        const pointToMeasureNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_POINT_TO_MEASURE] });
        if (pointToMeasureNodes.length > 0) {
          pointToMeasureLoader.onMapRightClick((pointToMeasureNodes[0] as Feature<Point>).properties!.id);
        }
      } else if (this.isEditPhotoPoint) {
        const pointToPhotoNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_POINT_TO_PHOTO] });
        if (pointToPhotoNodes.length > 0) {
          pointToPhotoLoader.onMapRightClick((pointToPhotoNodes[0] as Feature<Point>).properties!.id);
        }
      } else if (this.isEditBacklogPoint) {
        const nodes = map.queryRenderedFeatures(e.point, { layers: [MLID_BACKLOG_CIRCLE, MLID_BACKLOG_POINT] });
        if (nodes.length) {
          ReactDOM.render(removeFunction((id: string) => {
            backlogPointLoader.onMapRightClick(id);
            this._popup!.remove();
          }, nodes[0].properties!.id), this._popupRoot);
          this._popup!.setLngLat(e.lngLat).setDOMContent(this._popupRoot).addTo(map);
        }
      } else if (this.editable) {
        if (polygonsLoader.editingPolygon) {
          const editorNodes = map.queryRenderedFeatures(e.point, {layers: [MLID_POLYGON_EDITOR]});
          if (editorNodes.length > 0) {
            polygonsLoader.editingPolygon.onMapRightClick((editorNodes[0] as Feature<Point>).properties!.id);
          }
        } else if (forbiddenAreasLoader.editingPolygon) {
          const editorNodes = map.queryRenderedFeatures(e.point, {layers: [MLID_FORBIDDEN_AREAS_EDITOR]});
          if (editorNodes.length > 0) {
            forbiddenAreasLoader.editingPolygon.onMapRightClick((editorNodes[0] as Feature<Point>).properties!.id);
          }
        } else if (regionsLoader.editor) {
          const regionEditorNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_REGION_EDITOR] }) as Array<Feature<Point>>;
          if (regionEditorNodes.length > 0) {
            regionsLoader.editor.onMapRightClick(regionEditorNodes[0].properties!.id);
          }
        }
        if (this.enableAddParkingLot && polygonGenerator.generating) {
          polygonGenerator.onMapRightClickedOriginalPoint();
        }
        if (this.enableAddRoadNet && roadNetGenerator.generating) {
          roadNetGenerator.onMapRightClickedOriginalPoint();
        }
      }
      this._refreshCursor();
    });

    map.on('mousemove', (e: EventData) => {
      const source = map.getSource(MSID_ORIGINAL_POINTS_FOCUSED) as GeoJSONSource;
      if (!this.pressingAltKey) {
        const originalPoints = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ORIGINAL_POINTS] });
        if (originalPoints.length > 0) {
          source.setData(originalPoints[0]);
          this._focusingOriginalPoint = originalPoints[0] as Feature<Point>;
        } else if (this._focusingOriginalPoint) {
          source.setData(emptyPointFeature);
          this._focusingOriginalPoint = undefined;
        }
      } else if (this._focusingOriginalPoint) {
        source.setData(emptyPointFeature);
        this._focusingOriginalPoint = undefined;
      }
      if (this.editable) {
        if (polygonGenerator.generating) {
          if (this._focusingOriginalPoint) {
            polygonGenerator.onMapMouseMove(this._focusingOriginalPoint.geometry.coordinates as ILngLat);
          } else {
            polygonGenerator.onMapMouseMove([e.lngLat.lng, e.lngLat.lat]);
          }
        }
        if (polygonsLoader.editingPolygon) {
          if (this._focusingOriginalPoint) {
            polygonsLoader.editingPolygon.onMapDrag(this._focusingOriginalPoint.geometry.coordinates as ILngLat);
          } else {
            polygonsLoader.editingPolygon.onMapDrag([e.lngLat.lng, e.lngLat.lat]);
          }
        }
        if (forbiddenAreasLoader.editingPolygon) {
          if (this._focusingOriginalPoint) {
            forbiddenAreasLoader.editingPolygon.onMapDrag(this._focusingOriginalPoint.geometry.coordinates as ILngLat);
          } else {
            forbiddenAreasLoader.editingPolygon.onMapDrag([e.lngLat.lng, e.lngLat.lat]);
          }
        }
        if (regionsLoader.editor) {
          if (this._focusingOriginalPoint) {
            regionsLoader.editor.onMapDrag(this._focusingOriginalPoint.geometry.coordinates as ILngLat);
          } else {
            regionsLoader.editor.onMapDrag(e.lngLat.toArray() as ILngLat);
          }
        }
        if (roadNetLoader.editor) {
          const nearRoadNetPoints = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT] })as Array<Feature<Point>>;
          const nearRoadNetLineStrings = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_EDITOR_LOCAL, MLID_ROAD_NET] });
          if (nearRoadNetPoints.length) {
            const curPoint = turf.point(e.lngLat.toArray() as ILngLat);
            const nearestPoint = turfNearestPoint(curPoint, turf.featureCollection(nearRoadNetPoints));
            roadNetLoader.editor.onMapDrag(nearestPoint.geometry!.coordinates as ILngLat);
          } else if (nearRoadNetLineStrings.length) {
            const lineStrings = nearRoadNetLineStrings.filter(item => item.properties!.id !== roadNetLoader.editorRemote)as Array<Feature<LineString>>;
            if (lineStrings.length > 0) {
              const point = turf.point(e.lngLat.toArray() as ILngLat);
              const latelyPoint = turfNearestPointOnLine(lineStrings[0] as Feature<LineString>, point) as Feature<Point>;
              roadNetLoader.editor.onMapDrag(latelyPoint.geometry.coordinates as ILngLat);
            }
          } else {
            roadNetLoader.editor.onMapDrag(e.lngLat.toArray() as ILngLat);
          }
        }
        if (roadNetGenerator.generating) {
          const nearRoadNetPoints = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT] })as Array<Feature<Point>>;
          const nearRoadNetLineStrings = map.queryRenderedFeatures(getMouseInteractionBounds(e.point), { layers: [MLID_ROAD_NET_EDITOR_LOCAL, MLID_ROAD_NET] });
          if (nearRoadNetPoints.length) {
            const curPoint = turf.point(e.lngLat.toArray() as ILngLat);
            const nearestPoint = turfNearestPoint(curPoint, turf.featureCollection(nearRoadNetPoints));
            roadNetGenerator.onMapMouseMove(nearestPoint.geometry!.coordinates as ILngLat);
          } else if (nearRoadNetLineStrings.length) {
            const lineStrings = nearRoadNetLineStrings.filter(item => item.properties!.id !== roadNetLoader.editorRemote)as Array<Feature<LineString>>;
            if (lineStrings.length > 0) {
              const point = turf.point(e.lngLat.toArray() as ILngLat);
              const latelyPoint = turfNearestPointOnLine(lineStrings[0] as Feature<LineString>, point) as Feature<Point>;
              roadNetGenerator.onMapMouseMove(latelyPoint.geometry.coordinates as ILngLat);
            }
          } else {
            roadNetGenerator.onMapMouseMove(e.lngLat.toArray() as ILngLat);
          }
        }
      }
      pointToMeasureLoader.onMapMouseMove(e.lngLat.toArray() as ILngLat);
      backlogPointLoader.onMapMouseMove(e.lngLat.toArray() as ILngLat);
      this._refreshCursor();
    });

    map.on('mousedown', (e: EventData) => {
      if (this.isEditMeasurePoint) {
        const pointToMeasureNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_POINT_TO_MEASURE] });
        if (pointToMeasureNodes.length > 0) {
          this._draggingNode = true;
          pointToMeasureLoader.onMapMouseDown((pointToMeasureNodes[0] as Feature<Point>).properties!.id);
        }
      } else if (this.isEditBacklogPoint) {
        const nodes = map.queryRenderedFeatures(e.point, { layers: [MLID_BACKLOG_POINT] });
        if (nodes.length > 0) {
          this._draggingNode = true;
          backlogPointLoader.onMapMouseDown((nodes[0] as Feature<Point>).properties!.id);
        }
      } else if (this.editable) {
        if (polygonsLoader.editingPolygon) {
          const editorNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_POLYGON_EDITOR] });
          if (editorNodes.length > 0) {
            if (polygonsLoader.editingPolygon) {
              polygonsLoader.editingPolygon.onMapDragStart((editorNodes[0] as Feature<Point>).properties!.id);
              this._draggingNode = true;
            }
          }
        }
        if (forbiddenAreasLoader.editingPolygon) {
          const editorNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_FORBIDDEN_AREAS_EDITOR] });
          if (editorNodes.length > 0) {
            if (forbiddenAreasLoader.editingPolygon) {
              forbiddenAreasLoader.editingPolygon.onMapDragStart((editorNodes[0] as Feature<Point>).properties!.id);
              this._draggingNode = true;
            }
          }
        }
        if (regionsLoader.editor) {
          const regionEditorNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_REGION_EDITOR] }) as Array<Feature<Point>>;
          if (regionEditorNodes.length > 0) {
            if (regionsLoader.editor) {
              regionsLoader.editor.onMapDragStart(regionEditorNodes[0].properties!.id);
              this._draggingNode = true;
            }
          }
        }
        if (roadNetLoader.editor) {
          const roadNetEditorNodes = map.queryRenderedFeatures(e.point, { layers: [MLID_ROAD_NET_EDITOR] }) as Array<Feature<Point>>;
          if (roadNetEditorNodes.length > 0) {
            if (roadNetLoader.editor) {
              roadNetLoader.editor.onMapDragStart(roadNetEditorNodes[0].properties!.id);
              this._draggingNode = true;
            }
          }
        }
      }
      this._refreshCursor();
    });

    map.on('mouseup', () => {
      this._draggingNode = false;
      if (polygonsLoader.editingPolygon) {
        polygonsLoader.editingPolygon.onMapDragEnd();
      }
      if (forbiddenAreasLoader.editingPolygon) {
        forbiddenAreasLoader.editingPolygon.onMapDragEnd();
      }
      if (regionsLoader.editor) {
        regionsLoader.editor.onMapDragEnd();
      }
      if (roadNetLoader.editor) {
        roadNetLoader.editor.onMapDragEnd();
      }
      pointToMeasureLoader.onMapMouseUp();
      backlogPointLoader.onMapMouseUp();
      this._refreshCursor();
    });

    map.on('moveend', () => {
      const center = this._map!.getCenter();
      this.center = [center.lng, center.lat] as ILngLat;
      this._onMapMoveEnd();
    });
    this._onMapMoveEnd();

    map.on('zoomend', () => {
      this.zoom = map.getZoom();
    });
    this.zoom = map.getZoom();

    this._onMouseEnterAndLeave(MLID_ORIGINAL_POINTS);
    this._onMouseEnterAndLeave(MLID_POLYGONS);
    this._onMouseEnterAndLeave(MLID_POLYGON_EDITOR, true);
    this._onMouseEnterAndLeave(MLID_REMOTE_POLYGONS);
    this._onMouseEnterAndLeave(MLID_FORBIDDEN_AREAS);
    this._onMouseEnterAndLeave(MLID_FORBIDDEN_AREAS_EDITOR, true);
    this._onMouseEnterAndLeave(MLID_REMOTE_FORBIDDENS);
    this._onMouseEnterAndLeave(MLID_POINT_TO_MEASURE, true);
    this._onMouseEnterAndLeave(MLID_REGION_EDITOR, true);
    this._onMouseEnterAndLeave(MLID_ROAD_NET_EDITOR, true);
    this._onMouseEnterAndLeave(MLID_ROAD_NET, true);
    this._onMouseEnterAndLeave(MLID_ROAD_NET_EDITOR_LOCAL, true);
    this._onMouseEnterAndLeave(MLID_BACKLOG_CIRCLE, true);
    this._onMouseEnterAndLeave(MLID_BACKLOG_POINT, true);

  }

  private _refreshCursor() {
    const canvas = this._map!.getCanvas();
    if (this._draggingNode) {
      canvas.style.cursor = '-webkit-grabbing';
    } else if (this._intersectLayers.length > 0 || polygonGenerator.generating || roadNetGenerator.generating) {
      canvas.style.cursor = 'pointer';
    } else {
      canvas.style.cursor = '';
    }
  }

  private _onMouseEnterAndLeave(layerId: string, preventDragPan = false) {
    const map = this._map!;
    map.on('mouseenter', layerId, () => {
      if (preventDragPan) {
        map.dragPan.disable();
      }
      if (!this._intersectLayers.includes(layerId)) {
        this._intersectLayers.push(layerId);
      }
      this._refreshCursor();
    });
    map.on('mouseleave', layerId, () => {
      if (preventDragPan) {
        map.dragPan.enable();
      }
      this._intersectLayers.splice(this._intersectLayers.indexOf(layerId), 1);
      this._refreshCursor();
    });
  }

  private _initOriginalPointsLayer() {
    const map = this._map!;
    map.addSource(MSID_ORIGINAL_POINTS, {
      type: 'geojson',
      data: originalPointsLoader.geojson,
    });
    map.addLayer({
      id: MLID_ORIGINAL_POINTS,
      source: MSID_ORIGINAL_POINTS,
      type: 'symbol',
      layout: {
        'text-field': '+',
        'text-allow-overlap': true,
        'text-size': 20,
        'text-font': ['Open Sans Regular'],
      },
      paint: {
        'text-color': [
          'step', ['get', 'resultType'],
          '#000',
          0, '#00ca35',
          1, '#94d945',
          2, '#ffff58',
          3, '#ffae42',
          4, '#ff062a',
          5, '#000',
        ],
      },
    });
    map.addSource(MSID_ORIGINAL_POINTS_FOCUSED, {
      type: 'geojson',
      data: emptyPointFeature,
    });
    map.addLayer({
      id: MLID_ORIGINAL_POINTS_FOCUSED,
      source: MSID_ORIGINAL_POINTS_FOCUSED,
      type: 'symbol',
      layout: {
        'text-field': '+',
        'text-allow-overlap': true,
        'text-size': 30,
        'text-font': ['Open Sans Regular'],
      },
      paint: {
        'text-color': '#1bff7d',
      },
    });
  }

  private _initPolygonGeneratorLayer() {
    const map = this._map!;
    map.addSource(MSID_POLYGON_GENERATOR_OUTLINE, {
      type: 'geojson',
      data: polygonGenerator.geojsonOutline,
    });
    map.addSource(MSID_POLYGON_GENERATOR_NAVILINE, {
      type: 'geojson',
      data: polygonGenerator.geojsonNaviline,
    });
    map.addLayer({
      id: MLID_POLYGON_GENERATOR_OUTLINE,
      source: MSID_POLYGON_GENERATOR_OUTLINE,
      type: 'line',
      paint: {
        'line-color': '#0f0',
        'line-width': 2,
      },
    });
    map.addLayer({
      id: MLID_POLYGON_GENERATOR_NAVILINE,
      source: MSID_POLYGON_GENERATOR_NAVILINE,
      type: 'line',
      paint: {
        'line-color': '#0f0',
        'line-width': 1,
        'line-dasharray': [2, 2, 6],
      },
    });
  }

  private _initPolygonsLayer() {
    const map = this._map!;
    map.addSource(MSID_POLYGONS, {
      type: 'geojson',
      data: polygonsLoader.geojsonPolygons,
    });
    map.addLayer({
      id: MLID_POLYGONS,
      source: MSID_POLYGONS,
      type: 'fill',
      paint: {
        'fill-color': '#ff0',
        'fill-opacity': .3,
        'fill-outline-color': '#0ff',
      },
    });
    map.addSource(MSID_POLYGON_EDITOR, {
      type: 'geojson',
      data: polygonsLoader.geojsonEditor,
    });
    map.addLayer({
      id: MLID_POLYGON_EDITOR,
      source: MSID_POLYGON_EDITOR,
      type: 'circle',
      paint: {
        'circle-radius': [
          'step', ['get', 'type'],
          1,
          0, 5,
          1, 3,
        ],
        'circle-color': '#14b9ff',
        'circle-opacity': .5,
      },
    });
  }

  private _initRemotePolygonsLayer() {
    const map = this._map!;
    map.addSource(MSID_REMOTE_POLYGONS_BUFFER, {
      type: 'geojson',
      data: polygonsLoader.geojsonRemotePolygonsBuffer,
    });
    map.addLayer({
      id: MLID_REMOTE_POLYGONS_BUFFER,
      source: MSID_REMOTE_POLYGONS_BUFFER,
      type: 'fill',
      paint: {
        'fill-color': '#ff9ef6',
        'fill-opacity': .3,
        'fill-outline-color': '#80fcff',
      },
    });
    map.addSource(MSID_REMOTE_POLYGONS, {
      type: 'geojson',
      data: polygonsLoader.geojsonRemotePolygons,
    });
    map.addLayer({
      id: MLID_REMOTE_POLYGONS,
      source: MSID_REMOTE_POLYGONS,
      type: 'fill',
      paint: {
        'fill-color': '#f0f',
        'fill-opacity': .3,
        'fill-outline-color': '#0ff',
      },
    });
    // 没有大区属性的停车区
    map.addSource(MSID_REMOTE_POLYGONS_NO_REGION_BUFFER, {
      type: 'geojson',
      data: polygonsLoader.geojsonRemotePolygonsNoRegionBuffer,
    });
    map.addLayer({
      id: MLID_REMOTE_POLYGONS_NO_REGION_BUFFER,
      source: MSID_REMOTE_POLYGONS_NO_REGION_BUFFER,
      type: 'fill',
      paint: {
        'fill-color': '#ff9584',
        'fill-opacity': .3,
        'fill-outline-color': '#ffeaaa',
      },
    });
    map.addSource(MSID_REMOTE_POLYGONS_NO_REGION, {
      type: 'geojson',
      data: polygonsLoader.geojsonRemoteNoRegionPolygons,
    });
    map.addLayer({
      id: MLID_REMOTE_POLYGONS_NO_REGION,
      source: MSID_REMOTE_POLYGONS_NO_REGION,
      type: 'fill',
      paint: {
        'fill-color': '#D92E2E',
        'fill-opacity': .8,
        'fill-outline-color': 'beige',
      },
    });
  }

  private _initRegionsLayer() {
    const map = this._map!;
    map.addSource(MSID_REGIONS, {
      type: 'geojson',
      data: regionsLoader.geojson,
    });
    map.addSource(MSID_REGION_EDITOR_POLYGON, {
      type: 'geojson',
      data: regionsLoader.geojsonEditingRegion,
    });
    map.addSource(MSID_REGION_EDITOR, {
      type: 'geojson',
      data: regionsLoader.geojsonEditor,
    });
    map.addSource(MSID_REGION_BUFFER, {
      type: 'geojson',
      data: regionsLoader.geojsonBuffer,
    });
    map.addLayer({
      id: MLID_REGIONS,
      source: MSID_REGIONS,
      type: 'line',
      paint: {
        'line-width': 5,
        'line-color': '#ff0',
        'line-dasharray': [2, 2, 6],
      },
    });
    map.addLayer({
      id: MLID_REGION_BUFFER,
      source: MSID_REGION_BUFFER,
      type: 'fill',
      paint: {
        'fill-color': '#ff0',
        'fill-opacity': .5,
      },
    });
    map.addLayer({
      id: MLID_REGION_EDITOR_POLYGON,
      source: MSID_REGION_EDITOR_POLYGON,
      type: 'line',
      paint: {
        'line-width': 2,
        'line-color': '#ff0',
      },
    });
    map.addLayer({
      id: MLID_REGION_EDITOR,
      source: MSID_REGION_EDITOR,
      type: 'circle',
      paint: {
        'circle-radius': [
          'step', ['get', 'type'],
          1,
          0, 5,
          1, 3,
        ],
        'circle-color': '#ff0073',
        'circle-opacity': .8,
      },
    });
  }

  // 更新多选的辅助描边
  private _updateMultiPolygonSelectorHelper() {
    const geoJsonData = {
      type: 'FeatureCollection',
      features: Array.from(new Set(this.multiPolygonSelector)).map(record => {
        record = JSON.parse(record);
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: record.polygon.geometry.coordinates[0],
          },
          properties: {},
        };
      }),
    };
    const map: any = this._map;
    const source = map.getSource('_multiPolygonSelectorHelper');
    if (source) { source.setData(geoJsonData); } else {
      map.addSource('_multiPolygonSelectorHelper', {
        type: 'geojson',
        data: geoJsonData,
      });
    }
    if (!map.getLayer('_multiPolygonSelectorHelperLayer') ) {
      map.addLayer({
        id: '_multiPolygonSelectorHelperLayer',
        type: 'line',
        source: '_multiPolygonSelectorHelper',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#FF5733',
          'line-width': 4,
        },
      }) ;
    }
  }

  private _initParkPointLayer() {
    const map = this._map!;
    map.addSource(MSID_PARK_POINT_USER, {
      type: 'geojson',
      data: parkPointLoader.geojsonUser,
    });
    map.addSource(MSID_PARK_POINT_STOCK, {
      type: 'geojson',
      data: parkPointLoader.geojsonStock,
    });
    map.addLayer({
      id: MLID_PARK_POINT_USER,
      source: MSID_PARK_POINT_USER,
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': '#ff052a',
      },
      layout: {
        visibility: this.isParkPointVisible ? 'visible' : 'none',
      },
    });
    map.addLayer({
      id: MLID_PARK_POINT_STOCK,
      source: MSID_PARK_POINT_STOCK,
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': '#002fff',
      },
      layout: {
        visibility: this.isParkPointVisible ? 'visible' : 'none',
      },
    });
  }

  private _initPointToMeasureLayer() {
    const map = this._map!;
    map.addSource(MSID_POINT_TO_MEASURE, {
      type: 'geojson',
      data: pointToMeasureLoader.geojson,
    });
    map.addLayer({
      id: MLID_POINT_TO_MEASURE,
      source: MSID_POINT_TO_MEASURE,
      type: 'symbol',
      layout: {
        'text-field': '×',
        'text-allow-overlap': true,
        'text-size': 30,
        'text-font': ['Open Sans Regular'],
        'visibility': 'none',
      },
      paint: {
        'text-color': '#fff071',
      },
    });
  }

  private _initPointToPhotoLayer() {
    const map = this._map!;
    map.addSource(MSID_POINT_TO_PHOTO, {
      type: 'geojson',
      data: pointToPhotoLoader.photoGeoJson,
    });
    map.addLayer({
      id: MLID_POINT_TO_PHOTO,
      source: MSID_POINT_TO_PHOTO,
      type: 'symbol',
      layout: {
        'text-field': '*',
        'text-allow-overlap': true,
        'text-size': 30,
        'text-font': ['Open Sans Regular'],
        'visibility': 'none',
      },
      paint: {
        'text-color': 'red',
      },
    });
  }

  private _initAfterStockPlaceLayer() {
    const map = this._map!;
    map.addSource(MSID_AFTER_STOCK_PLACE, {
      type: 'geojson',
      data: pointToPhotoLoader.afterStockPlaceGeoJson,
    });
    map.addLayer({
      id: MLID_AFTER_STOCK_PLACE,
      source: MSID_AFTER_STOCK_PLACE,
      type: 'symbol',
      layout: {
        'text-field': '*',
        'text-allow-overlap': true,
        'text-size': 30,
        'text-font': ['Open Sans Regular'],
        'visibility': 'none',
      },
      paint: {
        'text-color': 'Gold',
      },
    });
  }

  private _initBeforeStockPlaceLayer() {
    const map = this._map!;
    map.addSource(MSID_BEFORE_STOCK_PLACE, {
      type: 'geojson',
      data: pointToPhotoLoader.beforeStockPlaceGeoJson,
    });
    map.addLayer({
      id: MLID_BEFORE_STOCK_PLACE,
      source: MSID_BEFORE_STOCK_PLACE,
      type: 'symbol',
      layout: {
        'text-field': '*',
        'text-allow-overlap': true,
        'text-size': 30,
        'text-font': ['Open Sans Regular'],
        'visibility': 'none',
      },
      paint: {
        'text-color': 'MediumSpringGreen',
      },
    });
  }

  private _initProhibitedAreasLayer() {
    const map = this._map!;
    map.addSource(MSID_PROHIBITED_AREAS, {
      type: 'geojson',
      data: prohibitedAreasLoader.geojson,
    });
    map.addLayer({
      id: MLID_PROHIBITED_AREAS,
      source: MSID_PROHIBITED_AREAS,
      type: 'line',
      paint: {
        'line-color': '#ccc',
        'line-width': 4,
        'line-dasharray': [2, 2],
      },
    });
  }

  private _initForbiddenAreasLayer() {
    const map = this._map!;
    map.addSource(MSID_FORBIDDEN_AREAS, {
      type: 'geojson',
      data: forbiddenAreasLoader.geojson,
    });
    // map.loadImage(
    //     require('../images/forbidden.png'),
    //     function (err:any, image:any) {
    //       if (err) throw err;
    //       map.addImage('pattern', image);
    //       map.addLayer({
    //         id: MLID_FORBIDDEN_AREAS,
    //         'type': 'fill',
    //         source: MSID_FORBIDDEN_AREAS,
    //         'paint': {
    //           'fill-pattern': 'pattern'
    //         }
    //       });
    //     }
    // );
    map.addLayer({
      id: MLID_FORBIDDEN_AREAS,
      source: MSID_FORBIDDEN_AREAS,
      type: 'fill',
      paint: {
        'fill-color': '#ff6b6b',
        'fill-opacity': .3,
        'fill-outline-color': '#ff0200',
      },
    });
    map.addSource(MSID_FORBIDDEN_AREAS_EDITOR, {
      type: 'geojson',
      data: forbiddenAreasLoader.geojsonEditor,
    });
    map.addLayer({
      id: MLID_FORBIDDEN_AREAS_EDITOR,
      source: MSID_FORBIDDEN_AREAS_EDITOR,
      type: 'circle',
      paint: {
        'circle-radius': [
          'step', ['get', 'type'],
          1,
          0, 5,
          1, 3,
        ],
        'circle-color': '#14b9ff',
        'circle-opacity': .5,
      },
    });
    map.setLayoutProperty(MLID_FORBIDDEN_AREAS, 'visibility', this.isForbiddenAreaVisible ? 'visible' : 'none');
  }

  private _initRemoteForbiddenAreasLayer() {
    const map = this._map!;
    map.addSource(MSID_REMOTE_FORBIDDENS, {
      type: 'geojson',
      data: forbiddenAreasLoader.geojsonRemotePolygons,
    });
    // map.loadImage(
    //     require('../images/forbidden.png'),
    //     function (err:any, image:any) {
    //       if (err) throw err;
    //       map.addImage('pattern', image);
    //       map.addLayer({
    //         id: MLID_REMOTE_FORBIDDENS,
    //         'type': 'fill',
    //         source: MSID_REMOTE_FORBIDDENS,
    //         'paint': {
    //           'fill-pattern': 'pattern'
    //         }
    //       });
    //     }
    // );

    map.addLayer({
      id: MLID_REMOTE_FORBIDDENS,
      source: MSID_REMOTE_FORBIDDENS,
      type: 'fill',
      paint: {
        'fill-color': '#031cff',
        'fill-opacity': .3,
        'fill-outline-color': '#fc15ff',
      },
    });
  }

  private _initInspectionAreasLayer() {
    const map = this._map!;
    map.addSource(MSID_INSPECTION_AREAS, {
      type: 'geojson',
      data: inspectionAreasLoader.geojson,
    });
    map.addLayer({
      id: MLID_INSPECTION_AREAS,
      source: MSID_INSPECTION_AREAS,
      type: 'line',
      paint: {
        'line-color': 'Aqua',
        'line-width': 3,
        'line-dasharray': [2, 2],
      },
    });
  }

  private _initRoadNetLineStringLayer() {
    const map = this._map!;
    map.addSource(MSID_ROAD_NET, {
      type: 'geojson',
      data: roadNetLoader.geojson,
    });
    map.addSource(MSID_ROAD_NET_EDITOR, {
      type: 'geojson',
      data: roadNetLoader.geojsonEditor,
    });
    map.addSource(MSID_ROAD_NET_EDITOR_LOCAL, {
      type: 'geojson',
      data: roadNetLoader.geojsonEditLocalRoadNet,
    });
    map.addSource(MSID_ROAD_NET_REMOTE_AND_LOCAL_POINT, {
      type: 'geojson',
      data: roadNetLoader.geojsonPoints,
    });
    map.addLayer({
      id: MLID_ROAD_NET,
      source: MSID_ROAD_NET,
      type: 'line',
      paint: {
        'line-color': 'SpringGreen',
        'line-width': 4,
      },
    });
    map.addLayer({
      id: MLID_ROAD_NET_EDITOR_LOCAL,
      source: MSID_ROAD_NET_EDITOR_LOCAL,
      type: 'line',
      paint: {
        'line-width': 4,
        'line-color': '#C6E2FF',
      },
    });
    map.addLayer({
      id: MLID_ROAD_NET_REMOTE_AND_LOCAL_POINT,
      source: MSID_ROAD_NET_REMOTE_AND_LOCAL_POINT,
      type: 'circle',
      layout: {
        visibility: 'none',
      },
      paint: {
        'circle-radius': 3,
        'circle-color': 'DarkOrange',
      },
    });
    map.addLayer({
      id: MLID_ROAD_NET_EDITOR,
      source: MSID_ROAD_NET_EDITOR,
      type: 'circle',
      paint: {
        'circle-radius': [
          'step', ['get', 'type'],
          1,
          0, 4,
          1, 2,
        ],
        'circle-color': '#14b9ff',
        'circle-opacity': .8,
      },
    });
  }

  private _initRoadNetGeneratorLayer() {
    const map = this._map!;
    map.addSource(MSID_ROAD_NET_GENERATOR_OUTLINE, {
      type: 'geojson',
      data: polygonGenerator.geojsonOutline,
    });
    map.addSource(MSID_ROAD_NET_GENERATOR_NAVILINE, {
      type: 'geojson',
      data: polygonGenerator.geojsonNaviline,
    });
    map.addLayer({
      id: MLID_ROAD_NET_GENERATOR_OUTLINE,
      source: MSID_ROAD_NET_GENERATOR_OUTLINE,
      type: 'line',
      paint: {
        'line-color': '#0f0',
        'line-width': 2,
      },
    });
    map.addLayer({
      id: MLID_ROAD_NET_GENERATOR_NAVILINE,
      source: MSID_ROAD_NET_GENERATOR_NAVILINE,
      type: 'line',
      paint: {
        'line-color': '#0f0',
        'line-width': 1,
        'line-dasharray': [2, 2, 6],
      },
    });
  }

  private _initHeatMap() {
    const map = this._map!;
    map.addSource(MSID_HEATMAP, {
      type: 'geojson',
      data: heatMapLoader.geojson,
    });
    map.addLayer({
      id: MLID_HEATMAP_POLYGON,
      source: MSID_HEATMAP,
      type: 'heatmap',
      paint: {
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(33,102,172,0)',
          0.2, 'rgb(103,169,207)',
          0.4, 'rgb(209,229,240)',
          0.6, 'rgb(253,219,199)',
          0.8, 'rgb(239,138,98)',
          1, 'rgb(178,24,43)',
        ],
        'heatmap-radius': 10,
      },
    });
    map.addLayer({
      id: MLID_HEATMAP_POINT,
      source: MSID_HEATMAP,
      type: 'circle',
      paint: {
        'circle-color': 'rgba(33,102,172,0)',
      },
    });
  }

  private _initDispatchWorkOrder() {
    const map = this._map!;

    map.addSource(MSID_DISPATCH_WORK_ORDER, {
      type: 'geojson',
      data: dispatchWorkOrderLoader.geojson,
    });
    map.addLayer({
      id: MLID_DISPATCH_WORK_ORDER,
      source: MSID_DISPATCH_WORK_ORDER,
      type: 'circle',
      paint: {
        'circle-radius': 9,
        'circle-color': '#ff7805',
        'circle-opacity': .3,
      },
      layout: {
        visibility: this.isDispatchWorkOrderVisible ? 'visible' : 'none',
      },
    });
  }

  private _initBacklogLayer() {
    const map = this._map!;
    map.addSource(MSID_BACKLOG_POINT, {
      type: 'geojson',
      data: backlogPointLoader.geojson,
    });
    map.addSource(MSID_BACKLOG_CIRCLE, {
      type: 'geojson',
      data: backlogPointLoader.geojsonBuffer,
    });
    map.addLayer({
      id: MLID_BACKLOG_CIRCLE,
      source: MSID_BACKLOG_CIRCLE,
      type: 'fill',
      paint: {
        'fill-color': '#fff',
        'fill-opacity': .8,
        'fill-outline-color': '#d34202',
      },
    });
    map.addLayer({
      id: MLID_BACKLOG_POINT,
      source: MSID_BACKLOG_POINT,
      type: 'symbol',
      layout: {
        'icon-image': ['get', 'warning'],
        'icon-offset': [10, -10],
      },
      paint: {},
    });

  }

  private _initAdjustmentLayer() {
    const map = this._map!;
    map.addSource(MSID_ADJUSTMENT_POINT, {
      type: 'geojson',
      data: adjustmentLoader.unFinishedGeojson,
    });
    map.addSource(MSID_ADJUSTMENT_POINT_FINISHED, {
      type: 'geojson',
      data: adjustmentLoader.finishedGeojson,
    });
    map.addLayer({
      id: MLID_ADJUSTMENT_POINT_FINISHED,
      source: MSID_ADJUSTMENT_POINT_FINISHED,
      type: 'circle',
      paint: {
        'circle-radius': 10,
        'circle-color': 'lightGreen',
      },
    });
    map.addLayer({
      id: MLID_ADJUSTMENT_POINT,
      source: MSID_ADJUSTMENT_POINT,
      type: 'circle',
      paint: {
        'circle-radius': 10,
        'circle-color': 'yellow',
      },
    });

  }

  private _initRoadLayer() {
    const map = this._map!;
    map.addSource(MSID_ROAD, {
      type: 'geojson',
      data: roadLoader.geojsonRoad,
    });
    map.addSource(MSID_ROAD_FURNITURE, {
      type: 'geojson',
      data: roadLoader.geojsonFurniture,
    });
    map.addSource(MSID_ROAD_LANE_DIVIDER, {
      type: 'geojson',
      data: roadLoader.geojsonLaneDivider,
    });
    map.addLayer({
      id: MLID_ROAD,
      source: MSID_ROAD,
      type: 'line',
      paint: {
        'line-color': 'green',
        'line-width': 1,
      },
    });
    map.addLayer({
      id: MLID_ROAD_FURNITURE,
      source: MSID_ROAD_FURNITURE,
      type: 'line',
      paint: {
        'line-color': 'blue',
        'line-width': 1,
      },
    });
    map.addLayer({
      id: MLID_ROAD_LANE_DIVIDER,
      source: MSID_ROAD_LANE_DIVIDER,
      type: 'line',
      paint: {
        'line-color': 'white',
        'line-width': 1,
      },
    });
  }

  private _initDemoLayer() {
    const map = this._map!;
    map.addSource('demo_source', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [[114.426132, 30.511639],
            [114.42614, 30.511637],
            [114.42614, 30.511641],
            [114.426094, 30.511644],
            [114.426025, 30.511652],
            [114.425957, 30.511658],
            [114.42588, 30.511662],
            [114.425804, 30.511667],
            [114.425735, 30.511675],
            [114.425674, 30.511679],
            [114.425613, 30.511683],
            [114.425552, 30.511686],
            [114.425476, 30.51169],
            [114.425392, 30.511698],
            [114.425316, 30.511698],
            [114.425247, 30.511703],
            [114.425179, 30.511709],
            [114.42511, 30.511711],
            [114.425034, 30.511713],
            [114.424973, 30.511717],
            [114.424911, 30.511717],
            [114.424843, 30.511723],
            [114.424774, 30.511726],
            [114.424698, 30.51173],
            [114.424614, 30.511738],
            [114.424553, 30.511742],
            [114.424484, 30.511749],
            [114.424431, 30.511753],
            [114.424362, 30.511757],
            [114.424309, 30.511766],
            [114.424255, 30.511763],
            [114.424187, 30.511766],
            [114.424126, 30.51177],
            [114.424065, 30.511774],
            [114.424004, 30.511778],
            [114.423935, 30.511784],
            [114.423866, 30.511791],
            [114.423798, 30.511793],
            [114.423729, 30.511799],
            [114.423668, 30.511801],
            [114.423599, 30.511803],
            [114.423523, 30.511808],
            [114.423454, 30.511812],
            [114.423386, 30.511816],
            [114.423317, 30.511824],
            [114.423241, 30.511826],
            [114.423172, 30.511827],
            [114.423088, 30.511837],
            [114.423012, 30.511841],
            [114.422951, 30.511847],
            [114.422874, 30.511854],
            [114.422806, 30.511858],
            [114.422737, 30.511864],
            [114.422646, 30.511873],
            [114.422577, 30.511879],
            [114.422501, 30.51189],
            [114.422432, 30.5119],
            [114.422363, 30.511908],
            [114.422005, 30.511868],
            [114.422089, 30.511868],
            [114.422165, 30.511866],
            [114.422249, 30.511866],
            [114.422325, 30.511866],
            [114.422401, 30.511868],
            [114.42247, 30.511864],
            [114.422539, 30.511856],
            [114.422615, 30.511854],
            [114.422638, 30.511852],
            [114.422707, 30.51185],
            [114.422791, 30.511852],
            [114.422859, 30.51185],
            [114.422935, 30.511843],
            [114.423012, 30.511839],
            [114.423111, 30.511837],
            [114.423187, 30.511833],
            [114.423271, 30.511827],
            [114.423347, 30.511822],
            [114.423424, 30.511814],
            [114.423508, 30.511808],
            [114.423599, 30.511803],
            [114.423676, 30.511799],
            [114.423752, 30.511799],
            [114.423828, 30.511791],
            [114.423897, 30.511786],
            [114.423981, 30.511784],
            [114.424057, 30.511778],
            [114.424126, 30.511774],
            [114.424194, 30.511763],
            [114.424263, 30.511757],
            [114.424324, 30.511744],
            [114.424385, 30.51174],
            [114.424454, 30.511736],
            [114.424515, 30.511732],
            [114.424591, 30.511726],
            [114.424667, 30.511726],
            [114.424736, 30.511726],
            [114.424812, 30.511717],
            [114.424881, 30.511713],
            [114.42495, 30.511707],
            [114.425011, 30.5117],
            [114.425087, 30.511692],
            [114.425171, 30.51169],
            [114.425232, 30.511677],
            [114.425308, 30.511675],
            [114.425385, 30.511673],
            [114.425453, 30.511675],
            [114.425529, 30.511671],
            [114.425621, 30.511667],
            [114.425705, 30.511663],
            [114.425781, 30.511656],
            [114.42585, 30.511652],
            [114.425919, 30.511646],
            [114.425987, 30.511642],
            [114.426071, 30.511635],
            [114.426125, 30.511635],
            [114.426071, 30.511642],
            [114.426003, 30.511648],
            [114.425934, 30.511654],
            [114.425865, 30.511658],
            [114.425774, 30.511663],
            [114.425705, 30.511669],
            [114.425636, 30.511675],
            [114.42556, 30.511677],
            [114.425491, 30.511679],
            [114.425438, 30.511688],
            [114.425354, 30.51169],
            [114.425293, 30.511694],
            [114.425232, 30.511696],
            [114.425163, 30.5117],
            [114.425087, 30.511702],
            [114.425003, 30.511703],
            [114.424927, 30.511709],
            [114.42485, 30.511715],
            [114.424782, 30.511723],
            [114.424706, 30.511723],
            [114.424637, 30.511726],
            [114.424545, 30.51174],
            [114.424477, 30.511749],
            [114.424408, 30.511753],
            [114.424339, 30.511763],
            [114.424294, 30.511763],
            [114.424217, 30.511766],
            [114.424126, 30.51177],
            [114.424057, 30.511776],
            [114.423988, 30.511782],
            [114.423912, 30.511789],
            [114.423836, 30.511797],
            [114.423759, 30.511803],
            [114.423676, 30.511805],
            [114.423607, 30.51181],
            [114.423531, 30.511816],
            [114.423462, 30.511818],
            [114.423393, 30.511822],
            [114.423317, 30.511826],
            [114.423225, 30.511829],
            [114.423157, 30.511835],
            [114.423088, 30.511835],
            [114.423027, 30.511841],
            [114.422974, 30.511848],
            [114.422913, 30.511852],
            [114.422829, 30.51186],
            [114.422768, 30.511868],
            [114.422707, 30.511875],
            [114.422653, 30.511879],
            [114.422592, 30.511889],
            [114.422516, 30.511896],
            [114.422455, 30.5119],
            [114.422417, 30.511896],
            [114.422363, 30.511902],
            [114.422333, 30.5119],
            [114.422264, 30.511902],
            [114.422188, 30.511906],

          ],
        },
        properties: {},
      },
    });
    map.addSource('demo_source_2', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [[114.426125, 30.511662],
            [114.426117, 30.511663],
            [114.426064, 30.511665],
            [114.425980, 30.511667],
            [114.425911, 30.511667],
            [114.425835, 30.511675],
            [114.425751, 30.511665],
            [114.425682, 30.511660],
            [114.425629, 30.511667],
            [114.425560, 30.511675],
            [114.425491, 30.511688],
            [114.425423, 30.511700],
            [114.425362, 30.511707],
            [114.425293, 30.511705],
            [114.425217, 30.511709],
            [114.425148, 30.511732],
            [114.425095, 30.511747],
            [114.425041, 30.511755],
            [114.424980, 30.511770],
            [114.424911, 30.511768],
            [114.424850, 30.511763],
            [114.424774, 30.511761],
            [114.424675, 30.511765],
            [114.424606, 30.511772],
            [114.424530, 30.511780],
            [114.424461, 30.511799],
            [114.424408, 30.511818],
            [114.424347, 30.511833],
            [114.424309, 30.511852],
            [114.424255, 30.511843],
            [114.424187, 30.511835],
            [114.424133, 30.511827],
            [114.424072, 30.511822],
            [114.424011, 30.511818],
            [114.423950, 30.511818],
            [114.423882, 30.511818],
            [114.423828, 30.511822],
            [114.423759, 30.511826],
            [114.423691, 30.511827],
            [114.423630, 30.511831],
            [114.423561, 30.511835],
            [114.423492, 30.511837],
            [114.423424, 30.511841],
            [114.423355, 30.511843],
            [114.423286, 30.511848],
            [114.423203, 30.511852],
            [114.423126, 30.511852],
            [114.423050, 30.511858],
            [114.422981, 30.511864],
            [114.422920, 30.511873],
            [114.422852, 30.511885],
            [114.422775, 30.511894],
            [114.422707, 30.511902],
            [114.422623, 30.511904],
            [114.422554, 30.511909],
            [114.422485, 30.511927],
            [114.422417, 30.511930],
            [114.422340, 30.511930],
            [114.422279, 30.511932],
            [114.422226, 30.511917],
            [114.422157, 30.511917],
            [114.422081, 30.511917],
            [114.422180, 30.512012],
            [114.422256, 30.511982],
            [114.422333, 30.511969],
            [114.422401, 30.511984],
            [114.422455, 30.512011],
            [114.422539, 30.512049],
            [114.422615, 30.512060],
            [114.422646, 30.512066],
            [114.422707, 30.512028],
            [114.422791, 30.512009],
            [114.422874, 30.511997],
            [114.422958, 30.511988],
            [114.423027, 30.511967],
            [114.423111, 30.511957],
            [114.423203, 30.511944],
            [114.423286, 30.511904],
            [114.423355, 30.511877],
            [114.423424, 30.511856],
            [114.423500, 30.511847],
            [114.423576, 30.511841],
            [114.423653, 30.511831],
            [114.423729, 30.511827],
            [114.423805, 30.511822],
            [114.423882, 30.511818],
            [114.423943, 30.511808],
            [114.424004, 30.511793],
            [114.424095, 30.511772],
            [114.424171, 30.511763],
            [114.424232, 30.511755],
            [114.424294, 30.511753],
            [114.424355, 30.511751],
            [114.424400, 30.511759],
            [114.424469, 30.511753],
            [114.424530, 30.511753],
            [114.424606, 30.511753],
            [114.424683, 30.511751],
            [114.424751, 30.511751],
            [114.424820, 30.511751],
            [114.424881, 30.511744],
            [114.424957, 30.511738],
            [114.425034, 30.511732],
            [114.425110, 30.511728],
            [114.425179, 30.511724],
            [114.425255, 30.511719],
            [114.425323, 30.511713],
            [114.425400, 30.511713],
            [114.425468, 30.511707],
            [114.425545, 30.511703],
            [114.425613, 30.511700],
            [114.425690, 30.511698],
            [114.425766, 30.511692],
            [114.425842, 30.511690],
            [114.425911, 30.511688],
            [114.425987, 30.511684],
            [114.426064, 30.511681],
            [114.426140, 30.511673],
            [114.426186, 30.511669],
            [114.426117, 30.511667],
            [114.426041, 30.511665],
            [114.425964, 30.511665],
            [114.425873, 30.511667],
            [114.425797, 30.511667],
            [114.425720, 30.511665],
            [114.425652, 30.511658],
            [114.425583, 30.511665],
            [114.425514, 30.511660],
            [114.425438, 30.511671],
            [114.425369, 30.511673],
            [114.425308, 30.511679],
            [114.425240, 30.511681],
            [114.425179, 30.511696],
            [114.425110, 30.511686],
            [114.425041, 30.511696],
            [114.424980, 30.511692],
            [114.424896, 30.511698],
            [114.424812, 30.511705],
            [114.424736, 30.511711],
            [114.424652, 30.511700],
            [114.424568, 30.511698],
            [114.424492, 30.511703],
            [114.424423, 30.511713],
            [114.424355, 30.511723],
            [114.424301, 30.511740],
            [114.424248, 30.511742],
            [114.424179, 30.511747],
            [114.424103, 30.511753],
            [114.424026, 30.511761],
            [114.423950, 30.511763],
            [114.423874, 30.511766],
            [114.423805, 30.511772],
            [114.423729, 30.511784],
            [114.423660, 30.511793],
            [114.423592, 30.511810],
            [114.423515, 30.511826],
            [114.423447, 30.511818],
            [114.423378, 30.511818],
            [114.423302, 30.511814],
            [114.423225, 30.511818],
            [114.423149, 30.511822],
            [114.423080, 30.511827],
            [114.423004, 30.511833],
            [114.422943, 30.511839],
            [114.422882, 30.511831],
            [114.422806, 30.511831],
            [114.422722, 30.511835],
            [114.422684, 30.511841],
            [114.422646, 30.511843],
            [114.422585, 30.511845],
            [114.422516, 30.511860],
            [114.422462, 30.511890],
            [114.422417, 30.511906],
            [114.422379, 30.511904],
            [114.422340, 30.511904],
            [114.422295, 30.511904],
            [114.422249, 30.511900],
            [114.422157, 30.511877],
            [114.422058, 30.511869],

          ],
        },
        properties: {},
      },
    });
    map.addSource('demo_source_3', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [114.426155, 30.511663],
            [114.426155, 30.511663],
            [114.426102, 30.511648],
            [114.426033, 30.511642],
            [114.425957, 30.511639],
            [114.425888, 30.511637],
            [114.425804, 30.511633],
            [114.425728, 30.511635],
            [114.425667, 30.511633],
            [114.425552, 30.511639],
            [114.425545, 30.511637],
            [114.425476, 30.511648],
            [114.425369, 30.511662],
            [114.425331, 30.511671],
            [114.425262, 30.511669],
            [114.425194, 30.511679],
            [114.425125, 30.511671],
            [114.425026, 30.511652],
            [114.424995, 30.511656],
            [114.424927, 30.511671],
            [114.424835, 30.511696],
            [114.424774, 30.511690],
            [114.424706, 30.511688],
            [114.424644, 30.511690],
            [114.424538, 30.511698],
            [114.424515, 30.511702],
            [114.424454, 30.511713],
            [114.424393, 30.511736],
            [114.424355, 30.511757],
            [114.424309, 30.511759],
            [114.424240, 30.511765],
            [114.424179, 30.511768],
            [114.424110, 30.511768],
            [114.424042, 30.511770],
            [114.423965, 30.511772],
            [114.423889, 30.511772],
            [114.423836, 30.511772],
            [114.423744, 30.511774],
            [114.423676, 30.511778],
            [114.423607, 30.511787],
            [114.423546, 30.511797],
            [114.423470, 30.511799],
            [114.423454, 30.511797],
            [114.423347, 30.511805],
            [114.423317, 30.511806],
            [114.423241, 30.511818],
            [114.423164, 30.511827],
            [114.423080, 30.511831],
            [114.422966, 30.511839],
            [114.422935, 30.511841],
            [114.422859, 30.511848],
            [114.422768, 30.511854],
            [114.422707, 30.511856],
            [114.422630, 30.511864],
            [114.422523, 30.511873],
            [114.422508, 30.511871],
            [114.422424, 30.511885],
            [114.422348, 30.511887],
            [114.422264, 30.511890],
            [114.422195, 30.511900],
            [114.422150, 30.511900],
            [114.422096, 30.511898],
            [114.422043, 30.511896],
            [114.422035, 30.511894],
            [114.422035, 30.511894],
            [114.421951, 30.511900],
            [114.421936, 30.511902],
            [114.421822, 30.511908],
            [114.421783, 30.511908],
            [114.422340, 30.511898],
            [114.422462, 30.511940],
            [114.422554, 30.511961],
            [114.422607, 30.511967],
            [114.422691, 30.511980],
            [114.422722, 30.511953],
            [114.422783, 30.511934],
            [114.422867, 30.511911],
            [114.422951, 30.511881],
            [114.423019, 30.511860],
            [114.423141, 30.511831],
            [114.423157, 30.511833],
            [114.423225, 30.511841],
            [114.423294, 30.511837],
            [114.423363, 30.511833],
            [114.423439, 30.511829],
            [114.423515, 30.511824],
            [114.423622, 30.511812],
            [114.423676, 30.511808],
            [114.423759, 30.511801],
            [114.423836, 30.511793],
            [114.423904, 30.511789],
            [114.423973, 30.511784],
            [114.424042, 30.511776],
            [114.424110, 30.511770],
            [114.424232, 30.511763],
            [114.424248, 30.511763],
            [114.424324, 30.511761],
            [114.424370, 30.511742],
            [114.424431, 30.511753],
            [114.424530, 30.511744],
            [114.424561, 30.511740],
            [114.424652, 30.511736],
            [114.424744, 30.511736],
            [114.424782, 30.511734],
            [114.424866, 30.511742],
            [114.424927, 30.511736],
            [114.425003, 30.511738],
            [114.425064, 30.511736],
            [114.425125, 30.511728],
            [114.425217, 30.511719],
            [114.425278, 30.511715],
            [114.425346, 30.511715],
            [114.425415, 30.511698],
            [114.425507, 30.511677],
            [114.425583, 30.511673],
            [114.425674, 30.511671],
            [114.425751, 30.511665],
            [114.425827, 30.511700],
            [114.425888, 30.511698],
            [114.426003, 30.511669],
            [114.426010, 30.511667],
            [114.426086, 30.511633],
            [114.426140, 30.511629],
            [114.425179, 30.511641],
            [114.425095, 30.511629],
            [114.425064, 30.511633],
            [114.424988, 30.511627],
            [114.424911, 30.511694],
            [114.424835, 30.511709],
            [114.424706, 30.511713],
            [114.424675, 30.511711],
            [114.424568, 30.511721],
            [114.424530, 30.511717],
            [114.424423, 30.511751],
            [114.424362, 30.511751],
            [114.424339, 30.511745],
            [114.424301, 30.511749],
            [114.424194, 30.511753],
            [114.424126, 30.511755],
            [114.423973, 30.511772],
            [114.423935, 30.511778],
            [114.423889, 30.511784],
            [114.423798, 30.511795],
            [114.423737, 30.511808],
            [114.423676, 30.511806],
            [114.423584, 30.511814],
            [114.423508, 30.511806],
            [114.423477, 30.511808],
            [114.423340, 30.511814],
            [114.423309, 30.511814],
            [114.423248, 30.511820],
            [114.423172, 30.511824],
            [114.423103, 30.511835],
            [114.423027, 30.511847],
            [114.422943, 30.511850],
            [114.422905, 30.511850],
            [114.422844, 30.511850],
            [114.422775, 30.511854],
            [114.422668, 30.511862],
            [114.422646, 30.511862],
            [114.422600, 30.511862],
            [114.422501, 30.511866],
            [114.422409, 30.511854],
            [114.422379, 30.511854],
            [114.422333, 30.511860],
            [114.422272, 30.511862],
            [114.422241, 30.511864],
            [114.422188, 30.511873],
            [114.422134, 30.511875],
            [114.422073, 30.511879],

          ],
        },
        properties: {},
      },
    });
    map.addSource('demo_source_4', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [[114.426166, 30.511654],
            [114.426164, 30.511655],
            [114.426160, 30.511656],
            [114.426153, 30.511658],
            [114.426134, 30.511663],
            [114.426122, 30.511662],
            [114.426107, 30.511665],
            [114.426092, 30.511664],
            [114.426076, 30.511665],
            [114.426062, 30.511663],
            [114.426044, 30.511663],
            [114.426025, 30.511664],
            [114.426011, 30.511664],
            [114.425996, 30.511664],
            [114.425980, 30.511664],
            [114.425964, 30.511666],
            [114.425948, 30.511666],
            [114.425935, 30.511666],
            [114.425921, 30.511666],
            [114.425906, 30.511666],
            [114.425893, 30.511665],
            [114.425877, 30.511664],
            [114.425863, 30.511664],
            [114.425848, 30.511665],
            [114.425834, 30.511666],
            [114.425816, 30.511670],
            [114.425801, 30.511670],
            [114.425786, 30.511669],
            [114.425769, 30.511671],
            [114.425756, 30.511668],
            [114.425740, 30.511667],
            [114.425724, 30.511667],
            [114.425708, 30.511668],
            [114.425692, 30.511668],
            [114.425676, 30.511669],
            [114.425660, 30.511671],
            [114.425642, 30.511674],
            [114.425624, 30.511676],
            [114.425606, 30.511679],
            [114.425589, 30.511679],
            [114.425575, 30.511677],
            [114.425553, 30.511678],
            [114.425536, 30.511679],
            [114.425522, 30.511680],
            [114.425505, 30.511684],
            [114.425490, 30.511685],
            [114.425474, 30.511685],
            [114.425459, 30.511685],
            [114.425444, 30.511685],
            [114.425428, 30.511686],
            [114.425413, 30.511687],
            [114.425396, 30.511691],
            [114.425381, 30.511694],
            [114.425366, 30.511694],
            [114.425351, 30.511694],
            [114.425337, 30.511693],
            [114.425322, 30.511693],
            [114.425306, 30.511696],
            [114.425290, 30.511699],
            [114.425275, 30.511702],
            [114.425258, 30.511704],
            [114.425244, 30.511706],
            [114.425229, 30.511708],
            [114.425216, 30.511707],
            [114.425200, 30.511708],
            [114.425185, 30.511709],
            [114.425169, 30.511710],
            [114.425153, 30.511712],
            [114.425137, 30.511712],
            [114.425122, 30.511714],
            [114.425108, 30.511715],
            [114.425093, 30.511715],
            [114.425078, 30.511717],
            [114.425063, 30.511717],
            [114.425048, 30.511719],
            [114.425032, 30.511721],
            [114.425017, 30.511720],
            [114.425001, 30.511721],
            [114.424986, 30.511721],
            [114.424969, 30.511722],
            [114.424953, 30.511724],
            [114.424938, 30.511725],
            [114.424920, 30.511728],
            [114.424902, 30.511733],
            [114.424886, 30.511735],
            [114.424872, 30.511735],
            [114.424857, 30.511735],
            [114.424842, 30.511736],
            [114.424827, 30.511736],
            [114.424813, 30.511736],
            [114.424795, 30.511741],
            [114.424781, 30.511739],
            [114.424766, 30.511739],
            [114.424750, 30.511741],
            [114.424735, 30.511740],
            [114.424720, 30.511740],
            [114.424705, 30.511738],
            [114.424690, 30.511736],
            [114.424675, 30.511735],
            [114.424659, 30.511735],
            [114.424644, 30.511734],
            [114.424628, 30.511735],
            [114.424612, 30.511737],
            [114.424595, 30.511737],
            [114.424580, 30.511736],
            [114.424564, 30.511735],
            [114.424550, 30.511733],
            [114.424534, 30.511734],
            [114.424518, 30.511735],
            [114.424500, 30.511739],
            [114.424485, 30.511738],
            [114.424470, 30.511739],
            [114.424457, 30.511740],
            [114.424445, 30.511741],
            [114.424431, 30.511742],
            [114.424418, 30.511743],
            [114.424404, 30.511744],
            [114.424389, 30.511747],
            [114.424374, 30.511751],
            [114.424359, 30.511750],
            [114.424343, 30.511755],
            [114.424328, 30.511757],
            [114.424312, 30.511755],
            [114.424298, 30.511753],
            [114.424285, 30.511749],
            [114.424272, 30.511749],
            [114.424260, 30.511747],
            [114.424243, 30.511749],
            [114.424227, 30.511751],
            [114.424212, 30.511752],
            [114.424199, 30.511753],
            [114.424187, 30.511752],
            [114.424172, 30.511751],
            [114.424158, 30.511749],
            [114.424142, 30.511751],
            [114.424127, 30.511751],
            [114.424112, 30.511752],
            [114.424095, 30.511756],
            [114.424079, 30.511758],
            [114.424063, 30.511761],
            [114.424046, 30.511762],
            [114.424031, 30.511763],
            [114.424014, 30.511765],
            [114.423998, 30.511767],
            [114.423982, 30.511767],
            [114.423967, 30.511768],
            [114.423951, 30.511769],
            [114.423936, 30.511770],
            [114.423920, 30.511771],
            [114.423906, 30.511771],
            [114.423889, 30.511771],
            [114.423874, 30.511770],
            [114.423859, 30.511771],
            [114.423844, 30.511771],
            [114.423830, 30.511771],
            [114.423818, 30.511770],
            [114.423804, 30.511772],
            [114.423789, 30.511773],
            [114.423775, 30.511774],
            [114.423760, 30.511776],
            [114.423746, 30.511777],
            [114.423731, 30.511778],
            [114.423715, 30.511780],
            [114.423699, 30.511782],
            [114.423684, 30.511783],
            [114.423670, 30.511783],
            [114.423655, 30.511784],
            [114.423641, 30.511784],
            [114.423627, 30.511785],
            [114.423612, 30.511786],
            [114.423598, 30.511786],
            [114.423583, 30.511788],
            [114.423567, 30.511790],
            [114.423554, 30.511791],
            [114.423538, 30.511793],
            [114.423523, 30.511794],
            [114.423505, 30.511799],
            [114.423489, 30.511802],
            [114.423474, 30.511804],
            [114.423458, 30.511806],
            [114.423444, 30.511807],
            [114.423429, 30.511807],
            [114.423413, 30.511808],
            [114.423398, 30.511810],
            [114.423383, 30.511811],
            [114.423368, 30.511811],
            [114.423352, 30.511812],
            [114.423336, 30.511813],
            [114.423320, 30.511815],
            [114.423305, 30.511816],
            [114.423289, 30.511817],
            [114.423273, 30.511818],
            [114.423258, 30.511819],
            [114.423242, 30.511821],
            [114.423226, 30.511823],
            [114.423213, 30.511819],
            [114.423198, 30.511819],
            [114.423182, 30.511821],
            [114.423166, 30.511821],
            [114.423150, 30.511823],
            [114.423136, 30.511821],
            [114.423122, 30.511821],
            [114.423105, 30.511822],
            [114.423090, 30.511822],
            [114.423073, 30.511824],
            [114.423057, 30.511826],
            [114.423040, 30.511827],
            [114.423025, 30.511827],
            [114.423007, 30.511830],
            [114.422991, 30.511830],
            [114.422975, 30.511832],
            [114.422958, 30.511833],
            [114.422951, 30.511819],
            [114.422939, 30.511814],
            [114.422925, 30.511813],
            [114.422908, 30.511815],
            [114.422892, 30.511818],
            [114.422877, 30.511822],
            [114.422862, 30.511821],
            [114.422847, 30.511823],
            [114.422832, 30.511824],
            [114.422817, 30.511823],
            [114.422802, 30.511824],
            [114.422786, 30.511821],
            [114.422769, 30.511820],
            [114.422751, 30.511823],
            [114.422738, 30.511818],
            [114.422720, 30.511819],
            [114.422699, 30.511825],
            [114.422683, 30.511829],
            [114.422666, 30.511826],
            [114.422650, 30.511826],
            [114.422634, 30.511827],
            [114.422615, 30.511839],
            [114.422606, 30.511831],
            [114.422587, 30.511833],
            [114.422569, 30.511836],
            [114.422553, 30.511838],
            [114.422533, 30.511846],
            [114.422519, 30.511848],
            [114.422503, 30.511850],
            [114.422487, 30.511851],
            [114.422469, 30.511856],
            [114.422453, 30.511860],
            [114.422436, 30.511864],
            [114.422419, 30.511867],
            [114.422402, 30.511867],
            [114.422386, 30.511876],
            [114.422370, 30.511872],
            [114.422357, 30.511871],
            [114.422343, 30.511876],
            [114.422330, 30.511887],
            [114.422317, 30.511890],
            [114.422301, 30.511881],
            [114.422288, 30.511872],
            [114.422276, 30.511864],
            [114.422267, 30.511860],
            [114.422266, 30.511857],
            [114.422268, 30.511854],
            [114.422267, 30.511857],
            [114.422274, 30.511862],
            [114.422282, 30.511847],
            [114.422297, 30.511848],
            [114.422312, 30.511848],
            [114.422325, 30.511838],
            [114.422339, 30.511836],
            [114.422351, 30.511829],
            [114.422359, 30.511808],
            [114.422371, 30.511799],
            [114.422383, 30.511790],
            [114.422397, 30.511786],
            [114.422411, 30.511789],
            [114.422428, 30.511793],
            [114.422447, 30.511802],
            [114.422466, 30.511811],
            [114.422479, 30.511815],
            [114.422498, 30.511824],
            [114.422512, 30.511825],
            [114.422528, 30.511827],
            [114.422544, 30.511832],
            [114.422559, 30.511833],
            [114.422573, 30.511833],
            [114.422589, 30.511834],
            [114.422604, 30.511834],
            [114.422620, 30.511835],
            [114.422636, 30.511835],
            [114.422653, 30.511834],
            [114.422669, 30.511835],
            [114.422684, 30.511835],
            [114.422699, 30.511835],
            [114.422714, 30.511835],
            [114.422729, 30.511835],
            [114.422746, 30.511834],
            [114.422759, 30.511834],
            [114.422774, 30.511834],
            [114.422790, 30.511833],
            [114.422804, 30.511833],
            [114.422818, 30.511832],
            [114.422832, 30.511832],
            [114.422847, 30.511831],
            [114.422859, 30.511831],
            [114.422871, 30.511833],
            [114.422882, 30.511832],
            [114.422896, 30.511830],
            [114.422910, 30.511828],
            [114.422926, 30.511826],
            [114.422941, 30.511824],
            [114.422956, 30.511823],
            [114.422972, 30.511823],
            [114.422986, 30.511822],
            [114.423003, 30.511823],
            [114.423020, 30.511824],
            [114.423036, 30.511825],
            [114.423052, 30.511826],
            [114.423068, 30.511827],
            [114.423085, 30.511828],
            [114.423106, 30.511830],
            [114.423125, 30.511831],
            [114.423144, 30.511832],
            [114.423163, 30.511832],
            [114.423181, 30.511832],
            [114.423198, 30.511832],
            [114.423216, 30.511830],
            [114.423232, 30.511828],
            [114.423249, 30.511827],
            [114.423267, 30.511826],
            [114.423283, 30.511825],
            [114.423299, 30.511823],
            [114.423315, 30.511822],
            [114.423332, 30.511821],
            [114.423349, 30.511820],
            [114.423366, 30.511819],
            [114.423383, 30.511819],
            [114.423400, 30.511818],
            [114.423416, 30.511818],
            [114.423432, 30.511818],
            [114.423450, 30.511819],
            [114.423469, 30.511821],
            [114.423489, 30.511823],
            [114.423506, 30.511824],
            [114.423546, 30.511825],
            [114.423576, 30.511833],
            [114.423607, 30.511849],
            [114.423566, 30.511847],
            [114.423551, 30.511844],
            [114.423540, 30.511839],
            [114.423540, 30.511836],
            [114.423547, 30.511832],
            [114.423557, 30.511831],
            [114.423569, 30.511829],
            [114.423582, 30.511828],
            [114.423596, 30.511825],
            [114.423609, 30.511824],
            [114.423624, 30.511822],
            [114.423639, 30.511821],
            [114.423654, 30.511819],
            [114.423669, 30.511818],
            [114.423684, 30.511817],
            [114.423700, 30.511816],
            [114.423715, 30.511815],
            [114.423731, 30.511814],
            [114.423747, 30.511812],
            [114.423764, 30.511812],
            [114.423779, 30.511810],
            [114.423793, 30.511808],
            [114.423809, 30.511806],
            [114.423824, 30.511805],
            [114.423838, 30.511804],
            [114.423854, 30.511803],
            [114.423869, 30.511803],
            [114.423883, 30.511801],
            [114.423897, 30.511800],
            [114.423914, 30.511799],
            [114.423930, 30.511798],
            [114.423945, 30.511796],
            [114.423962, 30.511795],
            [114.423978, 30.511794],
            [114.423993, 30.511792],
            [114.424008, 30.511791],
            [114.424023, 30.511789],
            [114.424038, 30.511788],
            [114.424053, 30.511788],
            [114.424068, 30.511787],
            [114.424084, 30.511786],
            [114.424099, 30.511787],
            [114.424115, 30.511786],
            [114.424131, 30.511785],
            [114.424148, 30.511784],
            [114.424163, 30.511781],
            [114.424179, 30.511780],
            [114.424193, 30.511778],
            [114.424208, 30.511776],
            [114.424220, 30.511782],
            [114.424240, 30.511793],
            [114.424263, 30.511804],
            [114.424346, 30.511817],
            [114.424570, 30.511765],
            [114.424582, 30.511767],
            [114.424606, 30.511782],
            [114.424626, 30.511763],
            [114.424648, 30.511759],
            [114.424658, 30.511763],
            [114.424644, 30.511773],
            [114.424662, 30.511764],
            [114.424681, 30.511752],
            [114.424704, 30.511745],
            [114.424714, 30.511757],
            [114.424731, 30.511755],
            [114.424751, 30.511747],
            [114.424769, 30.511742],
            [114.424792, 30.511744],
            [114.424806, 30.511741],
            [114.424826, 30.511738],
            [114.424850, 30.511734],
            [114.424860, 30.511742],
            [114.424874, 30.511713],
            [114.424906, 30.511712],
            [114.424930, 30.511711],
            [114.424942, 30.511714],
            [114.424963, 30.511714],
            [114.424978, 30.511712],
            [114.424994, 30.511712],
            [114.425009, 30.511713],
            [114.425027, 30.511713],
            [114.425042, 30.511712],
            [114.425060, 30.511713],
            [114.425077, 30.511713],
            [114.425094, 30.511712],
            [114.425110, 30.511711],
            [114.425125, 30.511710],
            [114.425142, 30.511711],
            [114.425158, 30.511710],
            [114.425171, 30.511710],
            [114.425187, 30.511711],
            [114.425202, 30.511711],
            [114.425218, 30.511712],
            [114.425237, 30.511711],
            [114.425254, 30.511709],
            [114.425270, 30.511709],
            [114.425284, 30.511707],
            [114.425301, 30.511706],
            [114.425316, 30.511704],
            [114.425333, 30.511703],
            [114.425348, 30.511702],
            [114.425362, 30.511701],
            [114.425378, 30.511700],
            [114.425392, 30.511698],
            [114.425408, 30.511696],
            [114.425424, 30.511695],
            [114.425440, 30.511693],
            [114.425456, 30.511691],
            [114.425473, 30.511690],
            [114.425489, 30.511690],
            [114.425503, 30.511690],
            [114.425519, 30.511691],
            [114.425537, 30.511690],
            [114.425552, 30.511687],
            [114.425569, 30.511687],
            [114.425585, 30.511685],
            [114.425600, 30.511680],
            [114.425615, 30.511679],
            [114.425631, 30.511676],
            [114.425645, 30.511674],
            [114.425656, 30.511668],
            [114.425677, 30.511671],
            [114.425693, 30.511671],
            [114.425710, 30.511670],
            [114.425724, 30.511670],
            [114.425739, 30.511669],
            [114.425754, 30.511668],
            [114.425769, 30.511668],
            [114.425784, 30.511667],
            [114.425800, 30.511665],
            [114.425815, 30.511664],
            [114.425831, 30.511664],
            [114.425852, 30.511661],
            [114.425871, 30.511658],
            [114.425889, 30.511655],
            [114.425907, 30.511654],
            [114.425925, 30.511654],
            [114.425943, 30.511652],
            [114.425961, 30.511651],
            [114.425977, 30.511651],
            [114.425995, 30.511649],
            [114.426013, 30.511648],
            [114.426030, 30.511647],
            [114.426048, 30.511647],
            [114.426065, 30.511646],
            [114.426081, 30.511644],
            [114.426097, 30.511642],
            [114.426112, 30.511641],
            [114.426127, 30.511640],
            [114.426143, 30.511638],
            [114.426158, 30.511636],
            [114.426172, 30.511635],
            [114.426178, 30.511637],
            [114.426161, 30.511637],
            [114.426145, 30.511633],
            [114.426127, 30.511630],
            [114.426112, 30.511629],
            [114.426098, 30.511629],
            [114.426083, 30.511629],
            [114.426068, 30.511630],
            [114.426053, 30.511630],
            [114.426037, 30.511631],
            [114.426021, 30.511633],
            [114.426006, 30.511633],
            [114.425992, 30.511635],
            [114.425977, 30.511636],
            [114.425961, 30.511637],
            [114.425945, 30.511639],
            [114.425929, 30.511639],
            [114.425913, 30.511639],
            [114.425898, 30.511640],
            [114.425883, 30.511642],
            [114.425870, 30.511642],
            [114.425855, 30.511643],
            [114.425838, 30.511644],
            [114.425822, 30.511645],
            [114.425807, 30.511646],
            [114.425794, 30.511645],
            [114.425781, 30.511645],
            [114.425697, 30.511671],
            [114.425700, 30.511668],
            [114.425702, 30.511666],
            [114.425697, 30.511665],
            [114.425686, 30.511664],
            [114.425672, 30.511665],
            [114.425658, 30.511665],
            [114.425644, 30.511665],
            [114.425630, 30.511665],
            [114.425618, 30.511666],
            [114.425604, 30.511667],
            [114.425590, 30.511668],
            [114.425575, 30.511670],
            [114.425562, 30.511669],
            [114.425550, 30.511668],
            [114.425537, 30.511667],
            [114.425522, 30.511666],
            [114.425508, 30.511665],
            [114.425495, 30.511665],
            [114.425480, 30.511665],
            [114.425466, 30.511665],
            [114.425452, 30.511666],
            [114.425439, 30.511667],
            [114.425425, 30.511667],
            [114.425409, 30.511667],
            [114.425393, 30.511667],
            [114.425378, 30.511667],
            [114.425362, 30.511667],
            [114.425346, 30.511668],
            [114.425330, 30.511669],
            [114.425315, 30.511670],
            [114.425298, 30.511669],
            [114.425283, 30.511668],
            [114.425272, 30.511666],
            [114.425259, 30.511666],
            [114.425245, 30.511667],
            [114.425231, 30.511667],
            [114.425216, 30.511668],
            [114.425202, 30.511668],
            [114.425182, 30.511671],
            [114.425169, 30.511671],
            [114.425154, 30.511672],
            [114.425140, 30.511673],
            [114.425126, 30.511674],
            [114.425110, 30.511676],
            [114.425097, 30.511674],
            [114.425082, 30.511674],
            [114.425068, 30.511675],
            [114.425051, 30.511677],
            [114.425037, 30.511677],
            [114.425022, 30.511680],
            [114.425007, 30.511680],
            [114.424993, 30.511682],
            [114.424976, 30.511682],
            [114.424961, 30.511684],
            [114.424944, 30.511686],
            [114.424930, 30.511686],
            [114.424913, 30.511688],
            [114.424898, 30.511689],
            [114.424879, 30.511695],
            [114.424861, 30.511697],
            [114.424844, 30.511699],
            [114.424827, 30.511700],
            [114.424810, 30.511702],
            [114.424794, 30.511702],
            [114.424780, 30.511703],
            [114.424766, 30.511705],
            [114.424751, 30.511706],
            [114.424737, 30.511708],
            [114.424724, 30.511709],
            [114.424709, 30.511709],
            [114.424693, 30.511711],
            [114.424678, 30.511711],
            [114.424662, 30.511711],
            [114.424643, 30.511714],
            [114.424625, 30.511716],
            [114.424614, 30.511714],
            [114.424599, 30.511715],
            [114.424588, 30.511715],
            [114.424561, 30.511727],
            [114.424544, 30.511731],
            [114.424526, 30.511734],
            [114.424511, 30.511737],
            [114.424493, 30.511740],
            [114.424479, 30.511743],
            [114.424466, 30.511743],
            [114.424453, 30.511744],
            [114.424439, 30.511743],
            [114.424425, 30.511743],
            [114.424411, 30.511745],
            [114.424396, 30.511747],
            [114.424382, 30.511751],
            [114.424369, 30.511756],
            [114.424354, 30.511760],
            [114.424338, 30.511763],
            [114.424331, 30.511758],
            [114.424324, 30.511751],
            [114.424310, 30.511749],
            [114.424297, 30.511749],
            [114.424283, 30.511751],
            [114.424269, 30.511752],
            [114.424254, 30.511754],
            [114.424239, 30.511756],
            [114.424225, 30.511755],
            [114.424211, 30.511754],
            [114.424197, 30.511753],
            [114.424183, 30.511753],
            [114.424169, 30.511752],
            [114.424156, 30.511753],
            [114.424141, 30.511755],
            [114.424129, 30.511751],
            [114.424115, 30.511752],
            [114.424096, 30.511755],
            [114.424081, 30.511755],
            [114.424064, 30.511757],
            [114.424046, 30.511759],
            [114.424029, 30.511760],
            [114.424013, 30.511760],
            [114.423997, 30.511761],
            [114.423979, 30.511763],
            [114.423963, 30.511764],
            [114.423946, 30.511766],
            [114.423929, 30.511767],
            [114.423915, 30.511768],
            [114.423896, 30.511770],
            [114.423882, 30.511770],
            [114.423867, 30.511772],
            [114.423855, 30.511772],
            [114.423844, 30.511771],
            [114.423831, 30.511770],
            [114.423816, 30.511772],
            [114.423802, 30.511772],
            [114.423789, 30.511772],
            [114.423773, 30.511772],
            [114.423758, 30.511774],
            [114.423744, 30.511775],
            [114.423730, 30.511775],
            [114.423715, 30.511776],
            [114.423700, 30.511777],
            [114.423684, 30.511778],
            [114.423669, 30.511780],
            [114.423653, 30.511781],
            [114.423636, 30.511782],
            [114.423621, 30.511783],
            [114.423608, 30.511783],
            [114.423594, 30.511784],
            [114.423578, 30.511785],
            [114.423563, 30.511787],
            [114.423548, 30.511787],
            [114.423532, 30.511790],
            [114.423519, 30.511790],
            [114.423505, 30.511790],
            [114.423490, 30.511789],
            [114.423475, 30.511789],
            [114.423461, 30.511789],
            [114.423446, 30.511790],
            [114.423430, 30.511791],
            [114.423416, 30.511791],
            [114.423400, 30.511792],
            [114.423385, 30.511794],
            [114.423370, 30.511795],
            [114.423355, 30.511796],
            [114.423340, 30.511797],
            [114.423324, 30.511797],
            [114.423309, 30.511799],
            [114.423295, 30.511801],
            [114.423281, 30.511801],
            [114.423267, 30.511803],
            [114.423252, 30.511807],
            [114.423238, 30.511808],
            [114.423221, 30.511811],
            [114.423205, 30.511812],
            [114.423189, 30.511814],
            [114.423173, 30.511815],
            [114.423157, 30.511816],
            [114.423140, 30.511818],
            [114.423128, 30.511816],
            [114.423114, 30.511815],
            [114.423097, 30.511817],
            [114.423084, 30.511817],
            [114.423070, 30.511817],
            [114.423052, 30.511822],
            [114.423037, 30.511824],
            [114.423023, 30.511826],
            [114.423010, 30.511827],
            [114.422996, 30.511828],
            [114.422983, 30.511828],
            [114.422967, 30.511831],
            [114.422952, 30.511834],
            [114.422936, 30.511835],
            [114.422920, 30.511837],
            [114.422902, 30.511842],
            [114.422887, 30.511841],
            [114.422873, 30.511838],
            [114.422855, 30.511844],
            [114.422839, 30.511846],
            [114.422822, 30.511849],
            [114.422807, 30.511849],
            [114.422787, 30.511858],
            [114.422772, 30.511860],
            [114.422757, 30.511860],
            [114.422743, 30.511860],
            [114.422728, 30.511861],
            [114.422717, 30.511856],
            [114.422703, 30.511855],
            [114.422689, 30.511855],
            [114.422675, 30.511854],
            [114.422662, 30.511853],
            [114.422649, 30.511850],
            [114.422636, 30.511849],
            [114.422622, 30.511843],
            [114.422610, 30.511841],
            [114.422598, 30.511840],
            [114.422583, 30.511842],
            [114.422569, 30.511843],
            [114.422555, 30.511852],
            [114.422538, 30.511857],
            [114.422522, 30.511854],
            [114.422510, 30.511853],
            [114.422494, 30.511852],
            [114.422481, 30.511844],
            [114.422478, 30.511841],
            [114.422481, 30.511836],
            [114.422483, 30.511834],
            [114.422482, 30.511833],
            [114.422476, 30.511833],
            [114.422462, 30.511835],
            [114.422444, 30.511838],
            [114.422427, 30.511842],
            [114.422415, 30.511841],
            [114.422401, 30.511842],
            [114.422385, 30.511845],
            [114.422369, 30.511846],
            [114.422351, 30.511850],
            [114.422339, 30.511849],
            [114.422327, 30.511852],
            [114.422315, 30.511856],
            [114.422298, 30.511859],
            [114.422273, 30.511856],
            [114.422257, 30.511855],
            [114.422255, 30.511853],
            [114.422253, 30.511853],
            [114.422254, 30.511855],
            [114.422256, 30.511861],
            [114.422257, 30.511864],
            [114.422249, 30.511868],
            [114.422253, 30.511882],
            [114.422243, 30.511867],
            [114.422243, 30.511868],
            [114.422235, 30.511857],
            [114.422234, 30.511854]],
        },
        properties: {},
      },
    });
    map.addSource('demo_source_5', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [114.426102, 30.511641],
            [114.426102, 30.511641],
            [114.426102, 30.511642],
            [114.426102, 30.511644],
            [114.426102, 30.511644],
            [114.426102, 30.511646],
            [114.426102, 30.511648],
            [114.426094, 30.511646],
            [114.426086, 30.511644],
            [114.426071, 30.511648],
            [114.426048, 30.511650],
            [114.426018, 30.511650],
            [114.425949, 30.511656],
            [114.425911, 30.511660],
            [114.425873, 30.511660],
            [114.425835, 30.511662],
            [114.425797, 30.511665],
            [114.425751, 30.511671],
            [114.425713, 30.511671],
            [114.425667, 30.511671],
            [114.425629, 30.511675],
            [114.425583, 30.511675],
            [114.425545, 30.511675],
            [114.425499, 30.511675],
            [114.425453, 30.511679],
            [114.425415, 30.511681],
            [114.425369, 30.511683],
            [114.425323, 30.511686],
            [114.425278, 30.511690],
            [114.425232, 30.511694],
            [114.425186, 30.511696],
            [114.425140, 30.511700],
            [114.425102, 30.511702],
            [114.425056, 30.511703],
            [114.425011, 30.511705],
            [114.424965, 30.511705],
            [114.424919, 30.511707],
            [114.424828, 30.511713],
            [114.424782, 30.511715],
            [114.424744, 30.511721],
            [114.424698, 30.511723],
            [114.424660, 30.511728],
            [114.424614, 30.511730],
            [114.424568, 30.511732],
            [114.424522, 30.511734],
            [114.424484, 30.511734],
            [114.424469, 30.511736],
            [114.424477, 30.511738],
            [114.424477, 30.511738],
            [114.424477, 30.511740],
            [114.424477, 30.511742],
            [114.424210, 30.511776],
            [114.424210, 30.511776],
            [114.424210, 30.511782],
            [114.424202, 30.511782],
            [114.424187, 30.511780],
            [114.424171, 30.511782],
            [114.424149, 30.511782],
            [114.424118, 30.511784],
            [114.424072, 30.511786],
            [114.424034, 30.511787],
            [114.423981, 30.511789],
            [114.423927, 30.511789],
            [114.423874, 30.511784],
            [114.423820, 30.511784],
            [114.423767, 30.511787],
            [114.423668, 30.511799],
            [114.423615, 30.511801],
            [114.423561, 30.511805],
            [114.423500, 30.511808],
            [114.423439, 30.511810],
            [114.423386, 30.511814],
            [114.423332, 30.511816],
            [114.423279, 30.511816],
            [114.423225, 30.511818],
            [114.423172, 30.511824],
            [114.423126, 30.511826],
            [114.423073, 30.511833],
            [114.423027, 30.511837],
            [114.422981, 30.511837],
            [114.422943, 30.511839],
            [114.422882, 30.511841],
            [114.422836, 30.511845],
            [114.422798, 30.511847],
            [114.422760, 30.511848],
            [114.422737, 30.511850],
            [114.422714, 30.511848],
            [114.422691, 30.511845],
            [114.422684, 30.511843],
            [114.422691, 30.511845],
            [114.422714, 30.511845],
            [114.422745, 30.511839],
            [114.422775, 30.511839],
            [114.422813, 30.511837],
            [114.422859, 30.511837],
            [114.422897, 30.511839],
            [114.422928, 30.511839],
            [114.422974, 30.511835],
            [114.423012, 30.511833],
            [114.423058, 30.511835],
            [114.423111, 30.511831],
            [114.423157, 30.511826],
            [114.423210, 30.511818],
            [114.423264, 30.511814],
            [114.423317, 30.511810],
            [114.423363, 30.511805],
            [114.423416, 30.511801],
            [114.423470, 30.511795],
            [114.423523, 30.511789],
            [114.423576, 30.511784],
            [114.423630, 30.511778],
            [114.423676, 30.511776],
            [114.423729, 30.511772],
            [114.423782, 30.511766],
            [114.423836, 30.511759],
            [114.423889, 30.511755],
            [114.423981, 30.511745],
            [114.424019, 30.511742],
            [114.424042, 30.511740],
            [114.424049, 30.511740],
            [114.424049, 30.511740],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511738],
            [114.424049, 30.511736],
            [114.424049, 30.511736],
            [114.424049, 30.511734],
            [114.424049, 30.511732],
            [114.424049, 30.511732],
            [114.424049, 30.511732],
            [114.424049, 30.511730],
            [114.424049, 30.511730],
            [114.424049, 30.511730],
            [114.424049, 30.511730],
            [114.424049, 30.511730],
            [114.424049, 30.511730],
            [114.424042, 30.511732],
          ],
        },
        properties: {},
      },
    });
    map.addLayer({
      id: 'demo_layer_line',
      source: 'demo_source',
      type: 'line',
      paint: {
        'line-color': '#00f',
      },
    });
    map.addLayer({
      id: 'demo_layer',
      source: 'demo_source',
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': '#0ff',
      },
    });
    map.addLayer({
      id: 'demo_layer_line_2',
      source: 'demo_source_2',
      type: 'line',
      paint: {
        'line-color': '#ff0',
      },
    });
    map.addLayer({
      id: 'demo_layer_2',
      source: 'demo_source_2',
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': '#f00',
      },
    });

    map.addLayer({
      id: 'demo_layer_3',
      source: 'demo_source_3',
      type: 'line',
      paint: {
        'line-color': 'green',
      },
    });
    map.addLayer({
      id: 'demo_layer_3_point',
      source: 'demo_source_3',
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': 'cyan',
      },
    });
    map.addLayer({
      id: 'demo_layer_4',
      source: 'demo_source_4',
      type: 'line',
      paint: {
        'line-color': 'red',
      },
    });
    map.addLayer({
      id: 'demo_layer_4_point',
      source: 'demo_source_4',
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': 'pink',
      },
    });

    map.addLayer({
      id: 'demo_layer_5',
      source: 'demo_source_5',
      type: 'line',
      paint: {
        'line-color': 'cyan',
      },
    });
    map.addLayer({
      id: 'demo_layer_5_point',
      source: 'demo_source_5',
      type: 'circle',
      paint: {
        'circle-radius': 2,
        'circle-color': 'blue',
      },
    });

    map.addSource('base_line', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [[114.42615999444445 * 2 - 114.42610000833334, 30.511642263888888 * 2 - 30.51164598888889], [114.42615999444445, 30.511642263888888], [114.42610000833334, 30.51164598888889], [114.42601367500001, 30.511651455555555], [114.42594765833334, 30.511655386111112], [114.42585802500001, 30.51166088888889], [114.42577467222223, 30.511665933333333], [114.42571505277779, 30.51166936388889], [114.42565475, 30.511673152777778], [114.4256109638889, 30.511675944444445], [114.42555806944445, 30.511679108333333], [114.42552627777778, 30.51168077222222], [114.42547900833334, 30.51168376388889], [114.42542269166667, 30.51168717222222], [114.42537092777778, 30.511690577777777], [114.4253092138889, 30.511694155555556], [114.42524637777778, 30.511697919444444], [114.42518437777778, 30.511701719444446], [114.4251215138889, 30.51170557777778], [114.42505963055557, 30.51170959722222], [114.42499670555556, 30.511713519444445], [114.42493432222223, 30.511717269444443], [114.42493410833333, 30.511717258333334], [114.42487123333333, 30.511721227777777], [114.42480154166667, 30.511725644444443], [114.42472776944444, 30.51173008888889], [114.42466568333334, 30.511733747222223], [114.42460330277778, 30.511737752777776], [114.42454092222222, 30.511741475], [114.42447820555556, 30.51174509166667], [114.4244158638889, 30.51174902777778], [114.42438453611112, 30.511750980555554], [114.424290525, 30.511756575], [114.4242282138889, 30.511760541666668], [114.42413353055557, 30.511766141666666], [114.42407130555556, 30.511769819444446], [114.42400849722223, 30.51177376111111], [114.42394597777778, 30.511777744444444], [114.42388334166667, 30.511781366666668], [114.42382064166667, 30.511785180555556], [114.42376566944445, 30.511789005555556], [114.42366444444446, 30.51179483611111], [114.42360224444445, 30.511798719444446], [114.42353965833334, 30.511802572222223], [114.42347717777778, 30.5118062], [114.42341442777779, 30.511809997222223], [114.42335156111112, 30.511814047222224], [114.42328935, 30.51181791388889], [114.42322692222223, 30.51182165], [114.42313307500001, 30.51182725], [114.42303766666667, 30.511833008333333], [114.422975925, 30.511836666666667], [114.42291300555556, 30.511840711111113], [114.42285058888889, 30.51184442222222], [114.42278806388889, 30.511848397222224], [114.42269452500001, 30.51185376388889], [114.42263220833334, 30.511857813888888], [114.42257054444445, 30.51186105277778], [114.42247121111112, 30.511867819444443],
            [114.42240468888889, 30.511871816666666],
            [114.42230986666667, 30.511877658333333],
            [114.42230986666667 * 10 - 114.42240468888889 * 9, 30.511877658333333 * 10 - 30.511871816666666 * 9],
          ],
        },
        properties: {},
      },
    });
    map.addLayer({
      id: 'base_line_layer',
      source: 'base_line',
      type: 'line',
      paint: {
        'line-width': 2,
        'line-color': 'black',
      },
    });

    const line1 = (map.getSource('demo_source') as GeoJSONSource & { _data: Feature<LineString> })._data;
    const line2 = (map.getSource('demo_source_2') as GeoJSONSource & { _data: Feature<LineString> })._data;
    const line3 = (map.getSource('demo_source_3') as GeoJSONSource & { _data: Feature<LineString> })._data;
    const line4 = (map.getSource('demo_source_4') as GeoJSONSource & { _data: Feature<LineString> })._data;
    const line5 = (map.getSource('demo_source_5') as GeoJSONSource & { _data: Feature<LineString> })._data;
    const baseline = (map.getSource('base_line') as GeoJSONSource & { _data: Feature<LineString> })._data;

    const func = (line: Feature<LineString>) => {
      const result = line.geometry.coordinates.reduce<{ dis: number[][], max: number, min: number, avg: number }>((memo, lngLat) => {
        const d = pointToLineDistance(lngLat, baseline) * 1000;
        memo.dis.push([lngLat[0], lngLat[1], d]);
        if (d > memo.max) {
          memo.max = d;
        }
        if (d < memo.min) {
          memo.min = d;
        }
        memo.avg = (d + (Number.isNaN(memo.avg) ? d : memo.avg)) / 2;
        return memo;
      }, { dis: [], max: 0, min: Infinity, avg: NaN });
      return result.dis.join('\n');
    };
    //
    // const r1 = func(line1);
    // const r2 = func(line2);
    // const r3 = func(line3);
    // const r4 = func(line4);
    // const r5 = func(line5);
    // console.log('1=-=-=-=-=-=-=-=');
    // console.log(r1);
    // console.log('2=-=-=-=-=-=-=-=');
    // console.log(r2);
    // console.log('3=-=-=-=-=-=-=-=');
    // console.log(r3);
    // console.log('4=-=-=-=-=-=-=-=');
    // console.log(r4);
    // console.log('5=-=-=-=-=-=-=-=');
    // console.log(r5);
    // console.log('-=-=-=-=-=-=-=-=');
  }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1-0!./src/stores/MapStore.ts